import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiAssetUrl, getRequest, addInterest, removeInterest, estimateTravel, downloadBothICS, getMyMusicianProfile, getMyTechProfile, adminHideRequest, adminUnhideRequest, toggleBookmark, updateInterestTravel, getRequestInterests, updateInterestStatus } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Heart, Users, Car, Train, Bike, Bookmark, Navigation, ShieldAlert, ShieldCheck, ArrowLeft, Phone, Cloud, CheckCircle2, Share2, Check, MapPin, Info, Music, ListMusic, Star, HandPlatter, Shirt, Sofa, Toilet, ShowerHead, LockKeyhole, Wifi, Beer, Home, Sun, CloudSun, Salad, Vegan, Building2, Utensils, NotebookText, ArrowUpRight, ChevronDown, ChevronRight, CalendarPlus, Share, SquarePen, Guitar, ListOrdered, Timeline, MicVocal, DoorOpen } from 'lucide-react';
import { computeLoadOut } from '../utils/gigTime';
import { cn, formatDate, formatRelativeTime, formatTime, getRateTypeLabel } from '../lib/utils';
import { readVenueName } from '../constants/profileContract';
import { toast } from 'sonner';
import { TravelConfirmationModal } from '../components/TravelConfirmationModal';

/* ── Helpers ───────────────────────────────────────────────── */

function timeToMinutes(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function calcDuration(a, b) {
  const s = timeToMinutes(a), e = timeToMinutes(b);
  if (s === null || e === null) return null;
  return e >= s ? e - s : (1440 - s) + e;
}

function fmtDur(min) {
  if (min == null) return null;
  const h = Math.floor(min / 60), m = min % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/* Countdown wording: "38 min" under an hour, then "1 h 12 m" ("2 h" on the hour). */
function fmtCountdown(mins) {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} m`;
}

/* Is `mins` (minutes after local midnight) inside an "HH:MM–HH:MM" availability window? A window
   whose end is earlier than its start crosses midnight (18:00–01:00). Anything that is not a
   plain time range answers false, so free-text hours never light up. */
function inTimeWindow(range, mins) {
  const m = /^(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})$/.exec(String(range || '').trim());
  if (!m) return false;
  const start = Number(m[1]) * 60 + Number(m[2]);
  const end = Number(m[3]) * 60 + Number(m[4]);
  if (start === end) return true;
  return start < end ? mins >= start && mins < end : mins >= start || mins < end;
}

/* Every time on a gig is the venue's wall-clock time, so "now" has to be read on the venue's
   clock too, not the device's: a musician in London or New York still gets Amsterdam's "today"
   and "available now". No request or venue carries a time zone of its own, so the zone is
   Europe/Amsterdam, the same one the backend and the calendar export use. */
const VENUE_TIME_ZONE = 'Europe/Amsterdam';
const venueClockFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: VENUE_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
/* Now in venue time: the venue's date ("2026-09-27") and minutes after the venue's midnight. */
function venueNow(at = new Date()) {
  const p = {};
  venueClockFormat.formatToParts(at).forEach(({ type, value }) => { p[type] = value; });
  return { date: `${p.year}-${p.month}-${p.day}`, minutes: (Number(p.hour) % 24) * 60 + Number(p.minute) };
}
/* Whole calendar days from one "YYYY-MM-DD" to another (+ when `to` is later), counted on dates
   alone so no zone or DST shift can round it. null when either is not a date. */
function daysBetween(from, to) {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(from || ''), b = /^(\d{4})-(\d{2})-(\d{2})/.exec(to || '');
  if (!a || !b) return null;
  return Math.round((Date.UTC(b[1], b[2] - 1, b[3]) - Date.UTC(a[1], a[2] - 1, a[3])) / 86400000);
}

/* Role & Execution enum label formatters */
const PERF_MODE_LABEL = {
  solo: 'Solo',
  duo: 'Duo',
  trio: 'Trio',
  quartet: 'Quartet',
  band: 'Band',
  backing_band_provided: 'Backing band provided',
};
const ROLE_TYPE_LABEL = {
  lead: 'Lead',
  support: 'Support',
  accompaniment: 'Accompaniment',
  house_band: 'House band',
};
/* Sentence case for a phrase that stands on its own line. formatRelativeTime is shared with
   DateWithRelative and the booking confirmation, where the same phrase runs on from other
   text and a capital mid-line would be wrong — so the case is applied where it is displayed,
   not in the helper. */
const sentenceCase = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : v);
/* Whole-label sentence case for a shared Title Case label shown on this page ("Flat Rate" →
   "Flat rate"). The shared vocabulary stays as it is for the pages that read it. */
const toSentence = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : v);

const BILL_POSITION_LABEL = {
  solo_bill: 'Only act',
  headliner: 'Headliner',
  direct_support: 'Direct support',
  opener: 'Opener',
  multi_act: 'Multi-act bill',
};
/* The facts line under the venue speaks in phrases that can only mean one thing. On a
   marketplace a bare "450" by a people icon reads as 450 people interested, and a bare "Band"
   as "band wanted", so each fact says what it is: "Audience of 450", "With a full band". */
const BILL_FACT = {
  solo_bill: 'Only act on the bill',
  headliner: 'Headline slot',
  direct_support: 'Direct support slot',
  opener: 'Opening slot',
  multi_act: 'Multi-act bill',
};
const LINEUP_FACT = {
  solo: 'Solo set',
  duo: 'In a duo',
  trio: 'In a trio',
  quartet: 'In a quartet',
  band: 'With a full band',
  backing_band_provided: 'Backing band provided',
};
const SETTING_FACT = {
  indoor: 'Indoors',
  outdoor: 'Outdoors',
  covered_outdoor: 'Outdoors, covered',
  mixed: 'Indoors and out',
};
const MATERIAL_LABEL = {
  own_setlist: 'Own setlist',
  provided_setlist: 'Provided setlist',
  mixed: 'Mixed',
  improvised: 'Improvised',
};
const ANNOUNCE_LABEL = {
  dutch: 'Dutch',
  english: 'English',
  both: 'Dutch + English',
  none: 'None',
};

function toSlug(title, venueName, city, eventDate) {
  let dp = '';
  if (eventDate) {
    try {
      const dt = new Date(eventDate + 'T00:00:00');
      const mo = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
      dp = mo[dt.getMonth()] + String(dt.getDate()).padStart(2, '0');
    } catch { /* date parse — non-critical, fall through to empty slug */ }
  }
  return `${(title||'').replace('[DEMO] ','')} ${venueName||''} ${city||''} ${dp}`
    .toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/-+/g,'-').replace(/^-|-$/g,'');
}

/* ── Urgency mapping (spec v2) ─────────────────────────────── */

function urgencyDisplay(urgency) {
  switch (urgency) {
    case 'CRITICAL': case 'TODAY':
      return { label: 'URGENT', cls: 'text-[var(--role-limit)] bg-[var(--role-limit)]/10 border-[var(--role-limit)]/25' };
    case 'THIS_WEEK':
      return { label: 'SOON', cls: 'text-[var(--t1)] bg-white/[0.03] border-[color:var(--s8-b2-solid)]' };
    default:
      return { label: 'PLANNED', cls: 'text-[var(--t3)] bg-white/[0.03] border-white/[0.06]' };
  }
}

/* ── Tiny sub-components ──────────────────────────────────── */

/* Canonical eyebrow label — condensed caps at the --s8-fs-label step.
   Single source of truth for every field/section micro-label. */
/* A time range whose numerals are DM Mono. No font's en dash is as long as a Mono digit, so
   between two times every one of them read as a hyphen. The separator is drawn instead
   (.rd-range-sep): one Mono cell wide, on the Mono dash's own height and stroke. The en dash
   itself stays in the text, transparent, so copying the range and screen readers still get
   "21:00–23:30". */
const TimeRange = ({ from, to }) => (
  <span style={{ whiteSpace: 'nowrap' }}>
    {from}<span className="rd-range-sep"><span className="rd-range-sep__char">{'\u2013'}</span></span>{to}
  </span>
);

/* A value the request leaves out. Words, not a bare dash (a lone "—" at the end of a row reads
   as a control): Barlow in the tertiary role (--t3) at the size of the value it stands in for,
   so a row keeps its height and rhythm. */
const NotGiven = () => (
  <span style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 400, color: 'var(--t3)', wordSpacing: 'normal' }}>Not given</span>
);

const EyebrowLabel = ({ children, className, style }) => (
  <span className={className} style={{ fontFamily: 'var(--s8-font-condensed)', fontSize: 'var(--s8-fs-label)', fontWeight: 600, letterSpacing: 'var(--s8-tracking-label)', textTransform: 'uppercase', color: 'var(--t2)', ...style }}>
    {children}
  </span>
);

const SectionHeader = ({ icon: Icon, children }) => (
  // Left-aligned: icon then title on one line. Full-cream title so it outranks its own
  // rows; the icon sits one step back at --t2, the same on every card.
  <h2 className="rd-section-title flex items-center gap-2.5 mb-5" style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: 'var(--rd-fs-card-title)', color: 'var(--t0)', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
    {Icon && <Icon className="h-5 w-5" style={{ color: 'var(--t2)' }} />}
    {children}
  </h2>
);

const DataPocket = ({ label, children, className }) => (
  <div className={cn('p-3', className)} style={{ background: 'var(--bg-card-inner)', border: '1px solid var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-md)' }}>
    <EyebrowLabel className="block mb-1">{label}</EyebrowLabel>
    {children}
  </div>
);

/* Label/value row for fact lists — label left, value right (or stacked for prose).
   Use inside a `divide-y` list so rows share one sparse divider instead of boxed pockets. */
const FACT_TONE = { ok: 'var(--role-covered)', money: 'var(--role-money)', limit: 'var(--role-limit)', muted: 'var(--t3)' };
/* `lead` sets the row at the page's card-row size, the Equipment rows' 18px (--s8-fs-lead) on
   1.35 leading, label and value alike; without it the row keeps 15px. At that size a side-by-
   side value never wraps or truncates: it keeps its width and the label gives way instead,
   wrapping under itself on the narrowest phones ("Travel covered within" beside "Not given"
   at 320px). The values beside a label are short by design; anything long is stacked. */
/* One number format for every figure on the page, the European one (nl-NL): 12.000 and
   €0,23. Fixed rather than the phone's language, so an audience never switches between
   12,000 and 12.000 from one phone to the next. */
const formatCount = (n) => Number(n).toLocaleString('nl-NL');
const formatCents = (n) => Number(n).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* A card's one-line summary: "10 tracks · 45m · 9 originals", "2 × 75m sets · 30m break".
   Each part is { num, word, time, color }. Mono means time on this page, so only a part marked
   time: true takes it; counts are body figures. Parts never break inside, only between. */
const SummaryLine = ({ parts, className, style, ...props }) => (
  <div className={cn('flex flex-wrap items-center gap-x-2', className)} style={{ fontFamily: 'var(--s8-font-body)', fontSize: 'var(--s8-fs-caption)', lineHeight: 1.5, color: 'var(--t3)', ...style }} {...props}>
    {parts.map((part, i) => (
      <React.Fragment key={`${part.num ?? ''}${part.word ?? ''}`}>
        {i > 0 && <span aria-hidden>·</span>}
        <span className="whitespace-nowrap" style={part.color ? { color: part.color } : undefined}>
          {part.num != null && <span style={part.time ? { fontFamily: 'var(--s8-font-mono)', wordSpacing: 'var(--rd-mono-word-gap)' } : { fontVariantNumeric: 'tabular-nums' }}>{part.num}</span>}
          {part.num != null && part.word && ' '}
          {part.word}
        </span>
      </React.Fragment>
    ))}
  </div>
);

const FactRow = ({ label, children, sub, tone, mono = false, stacked = false, labelColor = 'var(--t2)', lead = false, ...props }) => (
  <div className={cn('py-2.5', !stacked && 'flex items-baseline justify-between gap-4', stacked && 'rd-fact-stacked')} {...props}>
    <span className={cn(lead && !stacked ? 'min-w-0' : 'shrink-0', stacked && 'block mb-0.5')} style={{ fontFamily: 'var(--s8-font-body)', fontSize: lead ? 'var(--s8-fs-lead)' : '15px', lineHeight: lead ? 1.35 : undefined, color: labelColor }}>{label}</span>
    <span className={cn(lead && !stacked ? 'shrink-0' : 'min-w-0', stacked ? 'block' : 'text-right')}>
      <span className="block" style={{ fontFamily: mono ? 'var(--s8-font-mono)' : 'var(--s8-font-body)', fontSize: lead ? 'var(--s8-fs-lead)' : '15px', lineHeight: lead ? 1.35 : 1.4, color: FACT_TONE[tone] || 'var(--t0)', wordSpacing: mono ? 'var(--rd-mono-word-gap)' : undefined, whiteSpace: mono || (lead && !stacked) ? 'nowrap' : undefined }}>{children}</span>
      {sub && <span className="block mt-0.5" style={{ fontFamily: 'var(--s8-font-body)', fontSize: 'var(--s8-fs-caption)', color: 'var(--t3)' }}>{sub}</span>}
    </span>
  </div>
);

/* `tier` ('standard' | 'anchor') opts a card into the page-scoped warm border + gradient
   (.rd-card in index.css); without it the card keeps the plain grey treatment. */
const Section = ({ children, className, style: extraStyle, tier, ...props }) => (
  <div
    className={cn('p-5', tier ? `rd-card rd-card--${tier}` : 'rd-card-plain', className)}
    style={extraStyle}
    {...props}
  >
    {children}
  </div>
);

/* ── Role & Execution card body (shared by desktop + mobile) ── */
const MetricLabel = ({ children }) => <EyebrowLabel className="block mb-1">{children}</EyebrowLabel>;
const MetricValue = ({ children, muted = false }) => (
  <div style={{ fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: 'var(--s8-fs-body)', color: muted ? 'var(--t3)' : 'var(--t0)' }}>
    {children ?? '—'}
  </div>
);
const Dash = () => <span style={{ color: 'var(--t3)' }}>—</span>;

/* ── Hospitality: enum label maps + card body ── */
const FOOD_LABEL = {
  not_provided: 'Not provided',
  snacks: 'Snacks',
  meal_provided: 'Meal provided',
  meal_voucher: 'Meal voucher',
  rider_specific: 'Per rider',
};
const DRESSING_LABEL = { none: 'None', shared: 'Shared', private: 'Private' };
const SECURE_STORAGE_LABEL = { none: 'None', lockable_room: 'Lockable room', safe: 'Safe', venue_supervised: 'Venue supervised' };
const WIFI_LABEL = { none: 'None', guest_network: 'Guest network', staff_network: 'Staff network', unknown: 'Unknown' };
const DRINKS_LABEL = { none: 'None', water_only: 'Water only', soft_drinks: 'Soft drinks', rider_specific: 'Per rider', open_bar: 'Open bar' };
const YESNO_LABEL = { no: 'No', yes: 'Yes' };
const GREENROOM_LABEL = { no: 'No', shared: 'Shared', private: 'Private' };

const DIM_VALUES = new Set(['none', 'no', 'not_provided']);

/* Hospitality: icon-strip labels (compact) */
const FOOD_STRIP_LABEL = {
  not_provided: 'No food',
  snacks: 'Snacks',
  meal_provided: 'Meal',
  meal_voucher: 'Voucher',
  rider_specific: 'Per rider',
};
/* Venue facilities: two groups, Food & Drink and Backstage, each under an amber caps head with an
   amber rule, as a ruled list of rows: a small icon, the label, the value on the right. The icons
   are there to find a row, not to judge it, so they are all one neutral tone; the value carries
   the state, in one of three: there (bold cream), ask first (light amber), not there (the whole
   row muted, value "None", moved to the end of its group in its usual order). Food a short walk
   away gets an amber card of its own at the head of Food & Drink. The setting (indoor, outdoor)
   is a fact about the gig, so it sits in the summary box beside Audience. The guest list is
   stored but not shown to the talent: it is the door's business. A field the venue left unset
   has no row. */
const FOOD_VALUE = { at_venue: ['At venue', 'ok'], on_request: ['On Request', 'ok'], voucher: ['Voucher', 'ok'], none: ['None', 'off'] };
// Gigs from before catering carry only hospitality.food.
const LEGACY_FOOD_VALUE = { meal_provided: ['Provided', 'ok'], meal_voucher: ['Voucher', 'ok'], not_provided: ['None', 'off'], snacks: ['Snacks', 'ok'], rider_specific: ['Per rider', 'ok'] };
/* Diet at the venue, or at the place nearby as far as the venue knows. `ask` is its own state:
   unknown is not unavailable. */
const DIET_VALUE = { available: ['Yes', 'ok'], unavailable: ['None', 'off'], ask: ['Ask first', 'ask'] };
// [key, icon, label, states]
const VENUE_ROWS = [
  ['dressingRoom', Shirt, 'Dressing Room', { private: ['Private', 'ok'], shared: ['Shared', 'ok'], none: ['None', 'off'] }],
  ['greenRoom', Sofa, 'Green Room', { private: ['Private', 'ok'], shared: ['Shared', 'ok'], no: ['None', 'off'] }],
  ['artistToilet', Toilet, 'Toilet', { separate: ['Artists Only', 'ok'], shared_with_public: ['Shared with audience', 'ok'] }],
  ['showerAccess', ShowerHead, 'Shower', { yes: ['Yes', 'ok'], no: ['None', 'off'] }],
  ['secureStorage', LockKeyhole, 'Storage', { lockable_room: ['Lockable room', 'ok'], safe: ['Safe', 'ok'], venue_supervised: ['Supervised', 'ok'], none: ['None', 'off'] }],
  ['wifi', Wifi, 'WiFi', { guest_network: ['Guest network', 'ok'], staff_network: ['Staff network', 'ok'], none: ['None', 'off'], unknown: ['Ask first', 'ask'] }],
];
const SETTING_VALUE = {
  indoor: [Home, 'Indoor'],
  outdoor: [Sun, 'Outdoor'],
  covered_outdoor: [CloudSun, 'Covered outdoor'],
  mixed: [DoorOpen, 'Indoor and outdoor'],
};
const FAC_VALUE = { ok: 'var(--t0)', ask: 'var(--s8-amber-light)', off: 'var(--s8-text-off)' };
const FAC_TYPE = { fontFamily: 'var(--s8-font-body)', fontSize: '15px', lineHeight: 1.35 };

/* One row: icon, label, value right, at least 44px tall. A row with text under it (the notes)
   tops its icon to the label instead of centring it, and stacks the text under the label. */
const FacilityRow = ({ icon: Icon, label, value, tone, sub }) => {
  const off = tone === 'off';
  return (
    <li className={cn('flex gap-3', sub ? 'items-start' : 'items-center')} style={{ minHeight: '44px', padding: sub ? '12px 0' : '8px 0' }}>
      <Icon aria-hidden strokeWidth={1.75} className="shrink-0" style={{ width: '18px', height: '18px', marginTop: sub ? '1px' : undefined, color: off ? 'var(--s8-icon-off)' : 'var(--s8-text-sub)' }} />
      <div className="min-w-0 flex-1 flex flex-col" style={{ gap: '3px' }}>
        <span style={{ ...FAC_TYPE, fontWeight: 400, color: off ? 'var(--s8-text-off)' : 'var(--s8-text-label)' }}>{label}</span>
        {sub && <span style={{ fontFamily: 'var(--s8-font-body)', fontSize: '13px', lineHeight: 1.4, color: 'var(--s8-text-sub)' }}>{sub}</span>}
      </div>
      {value && <span className="shrink-0 text-right whitespace-nowrap" style={{ ...FAC_TYPE, fontWeight: off ? 500 : 600, color: FAC_VALUE[tone] }}>{value}</span>}
    </li>
  );
};

/* Group head: amber caps, then an amber rule to the edge. The one place on this page amber
   marks a title (see the colour roles in index.css). */
const FacilityGroup = ({ title, children, ...rest }) => (
  <div {...rest}>
    <h3 className="flex items-center" style={{ gap: '10px', marginBottom: '10px', fontFamily: 'var(--s8-font-body)', fontSize: '13px', fontWeight: 600, lineHeight: 1.2, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--s8-amber)' }}>
      <span className="shrink-0">{title}</span>
      <span aria-hidden className="flex-1" style={{ height: '1px', background: 'var(--s8-amber-line)' }} />
    </h3>
    {children}
  </div>
);

const FacilityRows = ({ rows }) => (
  <ul className="divide-y divide-[color:var(--s8-b1-solid)]">
    {rows.map((r) => <FacilityRow key={r.key} {...r} />)}
  </ul>
);

/* Food a short walk away: the place, when it closes, the venue's note as written, and the walk
   as a solid pill. Each line only when its field is set; without a place name the card is
   titled "Food Nearby", so it never opens on a bare time or note. */
const NearbyFoodCard = ({ nearby }) => (
  <div className="flex items-start" style={{ gap: '12px', padding: '16px', marginBottom: '4px', background: 'var(--s8-amber-surface)', border: '1px solid var(--s8-amber-line)', borderRadius: 'var(--s8-radius-lg)' }} data-testid="facilities-nearby">
    <Utensils aria-hidden strokeWidth={1.75} className="shrink-0" style={{ width: '18px', height: '18px', marginTop: '3px', color: 'var(--s8-amber)' }} />
    <div className="min-w-0 flex-1 flex flex-col" style={{ gap: '4px', fontFamily: 'var(--s8-font-body)' }}>
      <span style={{ fontSize: '18px', fontWeight: 600, lineHeight: 1.25, color: 'var(--t0)' }}>{nearby.place || 'Food Nearby'}</span>
      {nearby.open_until && <span style={{ fontSize: '14px', fontWeight: 400, lineHeight: 1.35, color: 'var(--s8-text-label)' }}>Open until {nearby.open_until}</span>}
      {nearby.note && <span style={{ fontSize: '13px', fontWeight: 400, lineHeight: 1.4, color: 'var(--s8-text-warm-muted)' }}>{nearby.note}</span>}
    </div>
    {nearby.walk_minutes != null && (
      <span className="shrink-0 inline-flex items-center whitespace-nowrap" style={{ gap: '5px', padding: '6px 10px', borderRadius: 'var(--s8-radius-pill)', background: 'var(--s8-amber)', color: 'var(--s8-bg-base)', fontFamily: 'var(--s8-font-body)', fontSize: '13px', fontWeight: 700, lineHeight: 1 }}>
        <MapPin aria-hidden style={{ width: '14px', height: '14px' }} />
        {nearby.walk_minutes} min
      </span>
    )}
  </div>
);

const hasVenueFacts = (hosp, catering) =>
  (hosp && Object.keys(hosp).length > 0) || !!catering;

// Rows that are there keep their order; what is not there follows, in its usual order.
const isThere = (r) => r.tone !== 'off';
const isOff = (r) => r.tone === 'off';

function VenueFactsStrip({ hosp, catering }) {
  if (!hasVenueFacts(hosp, catering)) return null;

  // Food & Drink
  const nearby = catering?.provided === 'nearby' ? catering.nearby : null;
  const showNearby = !!nearby && ['place', 'walk_minutes', 'open_until', 'note'].some((k) => nearby[k] != null && nearby[k] !== '');
  const food = [];
  const foodValue = catering ? FOOD_VALUE[catering.provided] : LEGACY_FOOD_VALUE[hosp?.food];
  if (foodValue) {
    const time = catering && ['at_venue', 'on_request'].includes(catering.provided) ? catering.served_at : null;
    food.push({ key: 'food', icon: HandPlatter, label: 'Food', value: time ? `${foodValue[0]}, ${time}` : foodValue[0], tone: foodValue[1] });
  }
  // Diet describes food the talent can get: the venue's meal, or the place nearby.
  if (['at_venue', 'on_request', 'nearby'].includes(catering?.provided)) {
    [['vegetarian', Salad, 'Vegetarian'], ['vegan', Vegan, 'Vegan']].forEach(([key, icon, label]) => {
      const v = DIET_VALUE[catering[key]];
      if (v) food.push({ key, icon, label, value: v[0], tone: v[1] });
    });
  }
  // Whether there are tokens at all; how many is not something to promise yet.
  if (hosp?.drinkTokens === true) food.push({ key: 'tokens', icon: Beer, label: 'Drink Tokens', value: 'Yes', tone: 'ok' });
  if (hosp?.drinkTokens === false) food.push({ key: 'tokens', icon: Beer, label: 'Drink Tokens', value: 'None', tone: 'off' });

  // Backstage: what is there, then the venue's notes, then what is not there.
  const venue = [];
  VENUE_ROWS.forEach(([key, icon, label, states]) => {
    const v = states[hosp?.[key]];
    if (v) venue.push({ key, icon, label, value: v[0], tone: v[1] });
  });
  const notes = hosp?.notes;
  const backstage = [
    ...venue.filter(isThere),
    ...(notes ? [{ key: 'notes', icon: NotebookText, label: 'Notes', sub: notes, tone: 'ok' }] : []),
    ...venue.filter(isOff),
  ];

  const hasFood = showNearby || food.length > 0;
  if (!hasFood && backstage.length === 0) return null;

  return (
    <div className="rd-card rd-card--standard" style={{ padding: '20px' }} data-testid="venue-facts-card">
      <SectionHeader icon={Building2}>Venue Facilities</SectionHeader>

      {/* One column at every width, capped so a label and its value never drift apart on a wide
          card. SectionHeader keeps its own 20px below; 4px more makes the 24px between sections. */}
      <div className="flex flex-col" style={{ maxWidth: '520px', gap: '24px', paddingTop: '4px' }}>
        {hasFood && (
          <FacilityGroup title="Food & Drink" data-testid="facilities-food">
            {showNearby && <NearbyFoodCard nearby={nearby} />}
            {food.length > 0 && <FacilityRows rows={[...food.filter(isThere), ...food.filter(isOff)]} />}
          </FacilityGroup>
        )}
        {backstage.length > 0 && (
          <FacilityGroup title="Backstage" data-testid="facilities-venue">
            <FacilityRows rows={backstage} />
          </FacilityGroup>
        )}
      </div>
    </div>
  );
}

function RoleExecutionBody({ exec }) {
  const e = exec || {};
  const sets = e.setsCount;
  const setLen = e.setLengthMinutes;
  const brk = e.breakLengthMinutes;
  const isSingleSet = sets === 1;

  const setLengthText = setLen != null
    ? (isSingleSet ? `${setLen} min` : `${setLen} min each`)
    : null;
  const breakText = brk != null ? `${brk} min` : null;
  const modeText = e.performanceMode ? PERF_MODE_LABEL[e.performanceMode] : null;
  const roleText = e.roleType ? ROLE_TYPE_LABEL[e.roleType] : null;
  const materialText = e.materialSource ? MATERIAL_LABEL[e.materialSource] : null;
  const annText = e.announcementLanguage ? ANNOUNCE_LABEL[e.announcementLanguage] : null;

  return (
    <div data-testid="role-execution-card">
      {/* Row 1: SETS / LENGTH / BREAK — collapses to 2-col when single-set */}
      <div className={cn('grid gap-x-4 gap-y-4 mb-4', isSingleSet ? 'grid-cols-2' : 'grid-cols-3')}>
        <div>
          <MetricLabel>Sets</MetricLabel>
          <MetricValue>{sets ?? <Dash />}</MetricValue>
        </div>
        <div>
          <MetricLabel>Length</MetricLabel>
          <MetricValue>{setLengthText || <Dash />}</MetricValue>
        </div>
        {!isSingleSet && (
          <div>
            <MetricLabel>Break</MetricLabel>
            <MetricValue>{breakText || <Dash />}</MetricValue>
          </div>
        )}
      </div>

      {/* Row 2: MODE / ROLE */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-4 mb-4">
        <div>
          <MetricLabel>Mode</MetricLabel>
          <MetricValue>{modeText || <Dash />}</MetricValue>
        </div>
        <div>
          <MetricLabel>Role</MetricLabel>
          <MetricValue>{roleText || <Dash />}</MetricValue>
        </div>
      </div>

      {/* Row 2b: BILL POSITION — full-width single value */}
      <div className="mb-4">
        <MetricLabel>Bill Position</MetricLabel>
        <MetricValue>{e.billPosition ? BILL_POSITION_LABEL[e.billPosition] : <Dash />}</MetricValue>
      </div>

      {/* Row 3: MATERIAL / ANNOUNCEMENTS */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-4">
        <div>
          <MetricLabel>Material</MetricLabel>
          <MetricValue>{materialText || <Dash />}</MetricValue>
        </div>
        <div>
          <MetricLabel>Announcements</MetricLabel>
          <MetricValue>{annText || <Dash />}</MetricValue>
        </div>
      </div>

      {/* Notes: full-width, only when present */}
      {e.notes && (
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--s8-b1-solid)' }}>
          <MetricLabel>Notes</MetricLabel>
          <div className="text-sm leading-relaxed" style={{ color: 'var(--t1)', fontFamily: 'var(--s8-font-body)' }} data-testid="role-execution-notes">
            {e.notes}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Genre Alignment Score Ring ────────────────────────────── */
function GenreScoreRing({ score }) {
  if (score == null) return null;
  const r = 18, c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  // Green for a strong match, bright neutral for a partial one: amber stays with money and the primary action.
  const color = score >= 70 ? 'var(--role-covered)' : score >= 40 ? 'var(--t1)' : 'var(--t3)';
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: 48, height: 48 }} data-testid="genre-score-ring">
      <svg width="48" height="48" viewBox="0 0 48 48" className="absolute">
        <circle cx="24" cy="24" r={r} fill="none" stroke="var(--s8-b1-solid)" strokeWidth="3" />
        <circle cx="24" cy="24" r={r} fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      <span style={{ fontFamily: 'var(--s8-font-mono)', fontSize: 'var(--s8-fs-caption)', fontWeight: 600, color }} data-testid="genre-score-value">{score}</span>
    </div>
  );
}

/* ── Travel Time Panel (calls Mapbox Directions API) ──────── */
// Parked until after launch — flip to true to render the Drive/Bike/Transit tiles in Logistics again.
const SHOW_TRAVEL_TIME_PANEL = false;

const TRAVEL_MODES = [
  { key: 'driving', icon: Car, label: 'Drive' },
  { key: 'cycling', icon: Bike, label: 'Bike' },
  { key: 'transit', icon: Train, label: 'Transit' },
];

function TravelTimePanel({ fromLat, fromLng, toLat, toLng }) {
  const [times, setTimes] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      const results = {};
      await Promise.allSettled(
        TRAVEL_MODES.map(async (m) => {
          try {
            const res = await fetch(`${API}/api/travel-time?from_lat=${fromLat}&from_lng=${fromLng}&to_lat=${toLat}&to_lng=${toLng}&mode=${m.key}`);
            if (res.ok) results[m.key] = await res.json();
          } catch (err) { console.debug('Travel time unavailable for mode:', m.key, err); }
        })
      );
      if (!cancelled) { setTimes(results); setLoading(false); }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [fromLat, fromLng, toLat, toLng, API]);

  if (loading) {
    return (
      <div className="mb-4 p-3.5 animate-pulse" style={{ background: 'var(--bg-card-inner)', border: '1px solid var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-md)' }}>
        <div className="h-4 w-24 mb-2" style={{ background: 'var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-sm)' }} />
        <div className="h-8 w-full" style={{ background: 'var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-sm)' }} />
      </div>
    );
  }

  if (Object.keys(times).length === 0) return null;

  return (
    <div className="mb-4" data-testid="travel-time-panel">
      <EyebrowLabel className="block mb-2">Your Travel</EyebrowLabel>
      <div className="grid grid-cols-3 gap-2">
        {TRAVEL_MODES.map(({ key, icon: Icon, label }) => {
          const data = times[key];
          if (!data) return (
            <div key={key} className="p-3 text-center" style={{ background: 'var(--bg-card-inner)', border: '1px solid var(--s8-b1-solid)', opacity: 0.4, borderRadius: 'var(--s8-radius-md)' }}>
              <Icon className="h-4 w-4 mx-auto mb-1" style={{ color: 'var(--t3)' }} />
              <div className="text-[10px]" style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--t3)' }}>{label}</div>
            </div>
          );
          return (
            <div key={key} className="p-3 text-center" style={{ background: 'var(--bg-card-inner)', border: '1px solid var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-md)' }} data-testid={`travel-mode-${key}`}>
              <Icon className="h-4 w-4 mx-auto mb-1" style={{ color: 'var(--t2)' }} />
              <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: 'var(--s8-fs-lead)', color: 'var(--t0)', lineHeight: 1 }}>
                {data.durationMinutes < 60 ? `${data.durationMinutes}` : `${Math.floor(data.durationMinutes / 60)}h${data.durationMinutes % 60 > 0 ? data.durationMinutes % 60 : ''}`}
              </div>
              <div className="text-[10px] mt-0.5" style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--t3)' }}>
                min · {data.distanceKm} km
              </div>
              {data.isEstimated && <div className="text-[9px] mt-0.5" style={{ color: 'var(--t3)' }}>est.</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */

/* Item type is set to the Day Timeline's labels. Both cards are read the same way — a list
   of things about the night, scanned once — so neither has a reason to be the smaller of
   the two. The marker box is not a round number: it is the height of one line of that text,
   which is what keeps the check optically centred on the first line when an item wraps. */
/* The token, not a px value, so the phone type floor raises both cards with the timeline.
   Fixed at 18px they fell a step behind it once the floor grew the lead size. */
const EQ_FS = 'var(--s8-fs-lead)';              // the item text, as the timeline's labels
const EQ_LH = 1.35;                             // the timeline's label leading
const EQ_MARK_BOX = `calc(${EQ_FS} * ${EQ_LH})`; // one line of ink (24.3px at 18px)
const EQ_MARK = 16;                             // px — the check glyph / the open box
/* Column heads (You bring, Venue provides) take the heading voice, a small echo of the card
   title: Bricolage 600 at the rows' own size (never under the rows it heads), sentence case, in the items' own --t1, so the card title stays
   the only cream heading on the card. (Barlow read too round here: it is this page's content
   voice, so a Barlow head read as one more row.) Only You
   bring carries a count, because only it says something to act on ("2 left"); it follows in
   Barlow 400 in --role-yours, tabular, like the timeline's "1 h 30 m left" (it used to be DM
   Mono, which set words in the numerals-only face). */
const EQ_HEAD = { fontFamily: 'var(--s8-font-display)', fontSynthesis: 'none', fontSize: EQ_FS, fontWeight: 600, letterSpacing: '-0.005em', lineHeight: 1.2574, color: 'var(--t1)' };
const EQ_COUNT = { fontFamily: 'var(--s8-font-body)', fontSize: EQ_FS, fontWeight: 400, lineHeight: 1.2574, fontVariantNumeric: 'tabular-nums' };

/* Motion blur for Show all / Show less, on the rows being revealed only (above the toggle): a
   short blur that swells with the motion's speed and clears as it settles (.rd-mblur-on, one
   CSS filter animation, run on the compositor). The toggle and the cards below it move
   without blur. Set at the tap, before the re-render, so it covers opening and closing
   alike. Not on reduced motion. */
function blurSetlistMotion(btn) {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const el = btn.closest('.rd-card')?.querySelector('.rd-collapse');
  if (!el) return;
  el.classList.remove('rd-mblur-on');
  void el.offsetWidth; // restart the animation on a quick second tap
  el.classList.add('rd-mblur-on');
  el.addEventListener('animationend', () => el.classList.remove('rd-mblur-on'), { once: true });
}

/* The setlist's remaining tracks and the key that expands them in place. Its own component
   holding its own open state, so a tap re-renders this and nothing else: with the state on the
   page, every tap re-rendered the whole page before the first frame (measured ~270ms at 6x CPU
   throttle, against ~50ms for the same change without React). The tracks stay mounted;
   .rd-collapse animates their height (grid 0fr → 1fr) so the list drops open from the preview
   rows, and they are inert while closed (no tab stops). The collapse clips its overflow, so it
   spans the card edge to edge and pads the list back in. The key is a stage key (.rd-key) pill,
   centred, 44px tall, --t1 text, --t0 on hover and pressed (a class, not inline, or the inline
   value would outrank the hover and pressed ones). */
/* Where the height moves in one step (the tile field in columns, see .rd-collapse in index.css),
   Show less would just vanish. There the rows first leave on their own (data-closing: a 220ms
   fade and 8px lift, transform and opacity only, so the columns are never rebalanced mid-way),
   then the height closes in one step. Which case applies is read from the CSS itself, the
   height transition being off, so the rule lives in one place. A second tap mid-way reverses. */
const SETLIST_LEAVE_MS = 220;

function SetlistMore({ total, children }) {
  const [open, setOpen] = useState(false);
  const collapseRef = useRef(null);
  const leaveTimer = useRef(null);
  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  const toggle = (btn) => {
    const el = collapseRef.current;
    if (leaveTimer.current) {
      // Tapped again while the rows were leaving: bring them back.
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
      delete el.dataset.closing;
      return;
    }
    const heightInOneStep = el && getComputedStyle(el).transitionDuration.split(',').every((d) => parseFloat(d) === 0);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (open && heightInOneStep && !reduced) {
      el.dataset.closing = 'true';
      leaveTimer.current = setTimeout(() => {
        leaveTimer.current = null;
        delete el.dataset.closing;
        setOpen(false);
      }, SETLIST_LEAVE_MS);
      return;
    }
    blurSetlistMotion(btn);
    setOpen((v) => !v);
  };

  return (
    <>
      <div ref={collapseRef} className="rd-collapse -mx-5" data-open={open} inert={!open}>
        <div>
          <div className="px-5">{children}</div>
        </div>
      </div>
      <div className="mt-3 flex justify-center">
        <button
          type="button"
          onClick={(e) => toggle(e.currentTarget)}
          aria-expanded={open}
          className="rd-key flex items-center justify-center gap-2 px-4 text-sm whitespace-nowrap outline-none text-[var(--t1)] hover:text-[var(--t0)] active:text-[var(--t0)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--role-money)]"
          style={{ minHeight: '44px', padding: '0 20px', borderRadius: 'var(--s8-radius-pill)' }}
          data-testid="setlist-toggle"
        >
          <span>{open ? 'Show Less' : `Show All ${total}`}</span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform duration-300 ease-out motion-reduce:transition-none', open && 'rotate-180')} aria-hidden />
        </button>
      </div>
    </>
  );
}

/* The calendar key's glyph: a calendar page with the gig's own day on it, so the key says which
   date it adds. Drawn on lucide's 24-unit grid and stroke, at the 18px both utility glyphs take
   (stroke 1.78 → the 1.33px lucide draws at 16px). The day is DM Mono, the page's data face. */
function DateLeaf({ day }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.78" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 2v4M16 2v4" />
      <rect x="3" y="4" width="18" height="18" rx="2.5" />
      <text x="12" y="18.4" textAnchor="middle" fill="currentColor" stroke="none" style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: '11px' }}>{day}</text>
    </svg>
  );
}

/* Add to calendar and Share. Each answers on the key itself: the glyph hands over to a green
   check that draws itself in, and the label says what happened ("Downloaded", "Shared", "Link
   copied") for 2s, then the key settles back. onPress returns that result, or nothing when
   there is nothing to confirm (a dismissed share sheet). Its own component holding its own
   state, like SetlistMore, so a tap re-renders this key and not the page. The done state is its
   own centred layer over the key, so each state centres on its own and the key keeps its width. */
function UtilityKey({ glyph, label, onPress, testId }) {
  const [done, setDone] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const press = async () => {
    const result = await onPress();
    if (!result) return;
    clearTimeout(timer.current);
    setDone(result);
    timer.current = setTimeout(() => setDone(null), 2000);
  };
  return (
    <button
      type="button"
      onClick={press}
      data-done={done ? 'true' : undefined}
      className="rd-ghost-btn rd-key rd-ukey relative flex items-center justify-center px-4 text-sm font-medium whitespace-nowrap outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--role-money)]"
      style={{ minHeight: '44px', borderRadius: '22px' }}
      data-testid={testId}
    >
      <span className="rd-ukey__rest flex items-center justify-center gap-2">
        <span className="rd-ukey__glyph" aria-hidden>{glyph}</span>
        <span>{label}</span>
      </span>
      <span className="rd-ukey__done flex items-center justify-center gap-2" aria-hidden>
        <svg className="rd-ukey__check" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.5l4.5 4.5L19 7.5" pathLength="1" />
        </svg>
        <span>{done || '\u00a0'}</span>
      </span>
      <span className="sr-only" aria-live="polite">{done || ''}</span>
    </button>
  );
}

/* You bring: the musician's packing checklist. Its own component holding its own ticks, for the
   same reason as SetlistMore: a tick used to re-render the whole page (~270ms before the first
   frame at 6x CPU throttle), which is what the gauge's glide was waiting behind. */
function PackingList({ gear, gigId, eventDayDelta }) {
  /* Packing ticks for "You bring": the musician's own checklist, kept on this device only
     (localStorage, one key per gig). Storage can be missing or throw (private mode, blocked
     site data), so every read and write is guarded and the boxes still work for the session. */
  const [packed, setPacked] = useState({});
  const packingKey = gigId ? `stage8:packing:${gigId}` : null;
  useEffect(() => {
    if (!packingKey) return;
    let saved = {};
    try { saved = JSON.parse(window.localStorage.getItem(packingKey) || '{}'); } catch { saved = {}; }
    setPacked(saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {});
  }, [packingKey]);
  const togglePacked = (itemKey) => {
    const next = { ...packed };
    if (next[itemKey]) delete next[itemKey]; else next[itemKey] = true;
    setPacked(next);
    try { if (packingKey) window.localStorage.setItem(packingKey, JSON.stringify(next)); } catch { /* storage unavailable: session state only */ }
  };
  const packedCount = gear.filter((g, i) => packed[`${i}:${g}`]).length;
  return (
    <div className="equip-col">
      <div className="flex items-baseline gap-2 mb-2">
        {/* Once every box is ticked the head itself becomes "All Packed!" and the count goes:
            the list is done, so the head says so instead of counting. Both words share
            one cell and crossfade (.rd-swap); only the shown one is read out. */}
        <h3 style={EQ_HEAD} className="rd-swap" data-testid="packing-head">
          <span data-on={packedCount < gear.length || undefined} aria-hidden={packedCount === gear.length || undefined}>You Bring</span>
          <span className="rd-swap__to" data-on={packedCount === gear.length || undefined} aria-hidden={packedCount < gear.length || undefined}>All Packed!</span>
        </h3>
        {/* "4" until something is ticked, then "2/4". On gig day, with anything
            unticked, it reads what is still to pack: "2 left". When the list is done it
            fades out with "You bring" (reading "0 left" or "4/4" on its way) rather than
            vanishing while the head is still fading. */}
        <span className="rd-fade" data-off={packedCount === gear.length || undefined} aria-hidden={packedCount === gear.length || undefined} style={{ ...EQ_COUNT, color: 'var(--role-yours)' }} data-testid="packing-count">
          {eventDayDelta === 0 ? `${gear.length - packedCount} left`
            : packedCount > 0 ? `${packedCount}/${gear.length}` : gear.length}
        </span>
      </div>
      {/* Packing progress: a 2px --role-yours fill on a full-width track, ticked / total.
          The track is what keeps it a gauge: a lone line stopping part-way across read as
          an underline of the head. The fill is scaled, not resized, so each tick glides
          (.rd-pack-fill); the row is always there, so the list never jumps. */}
      <div aria-hidden className="rd-pack-track mb-2">
        <div className="rd-pack-fill" data-testid="packing-progress" style={{ transform: `scaleX(${packedCount / gear.length})` }} />
      </div>
      <ul>
        {gear.map((g, i) => {
          const itemKey = `${i}:${g}`;
          const ticked = !!packed[itemKey];
          return (
            <li key={`ag-${g}-${i}`}>
              {/* A native checkbox inside a label: the whole row is the 44px tap target,
                  the item text is its accessible name, and Space toggles it from the
                  keyboard. The input is visually hidden; the square is drawn beside it. */}
              <label
                className="flex items-start gap-2.5 py-2.5 cursor-pointer outline-none has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[color:var(--role-money)]"
                style={{ minHeight: '44px', borderRadius: 'var(--s8-radius-sm)' }}
                data-testid={`packing-item-${i}`}
              >
                <input type="checkbox" className="sr-only" checked={ticked} onChange={() => togglePacked(itemKey)} />
                <span aria-hidden className="shrink-0 flex items-center justify-center" style={{ width: `${EQ_MARK + 1}px`, height: EQ_MARK_BOX }}>
                  {/* Unticked: an empty square on a hairline in secondary text colour.
                      Ticked: filled with --role-yours, a dark check inside. */}
                  <span
                    className="flex items-center justify-center"
                    style={{
                      width: `${EQ_MARK}px`, height: `${EQ_MARK}px`, borderRadius: 'var(--s8-radius-xs)',
                      border: ticked ? '1.5px solid var(--role-yours)' : '1.5px solid var(--t1)',
                      background: ticked ? 'var(--role-yours)' : 'transparent',
                    }}
                  >
                    {ticked && <Check style={{ width: '12px', height: '12px', color: 'var(--s8-bg-base)', strokeWidth: 3 }} />}
                  </span>
                </span>
                {/* Same face, size and weight as Venue provides; only the colour says
                    it is yours. Once packed it steps down to secondary text. */}
                <span style={{ fontFamily: 'var(--s8-font-body)', fontSize: EQ_FS, lineHeight: EQ_LH, fontWeight: 400, color: ticked ? 'var(--t1)' : 'var(--role-yours)' }}>{g}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* The gig page's loading state, exported so the share link (/gig/:slug), which resolves to a
   gig and then opens it, shows the same skeleton rather than a spinner. */
export function RequestDetailSkeleton({ embedded = false }) {
  return (
    <div
      className={cn('request-detail-page', !embedded && 'rd-page--docked')}
      style={{ fontFamily: 'var(--s8-font-body)' }}
      data-testid="request-detail-page"
    >
    <div className="rd-drill drill-slide-in">
      <div className="rd-skeleton" data-testid="request-detail-skeleton">
        <span className="sr-only" role="status">Loading gig details</span>
        {/* Shaped like the composition it stands in for, in its order: two title lines, the
            venue line, the rule that closes the section, then cards. The
            bar used to be drawn at the top here — it is docked at the foot now, so that shape
            moved with it. */}
        <div className="rd-skeleton__title" aria-hidden />
        <div className="rd-skeleton__title rd-skeleton__title--short" aria-hidden />
        <div className="rd-skeleton__line" aria-hidden />
        <div className="rd-skeleton__rule" aria-hidden />
        <div className="rd-skeleton__card" aria-hidden />
        <div className="rd-skeleton__card rd-skeleton__card--short" aria-hidden />
      </div>
      {!embedded && (
        <div className="rd-skeleton__dock" aria-hidden>
          <div className="rd-skeleton__icon" />
          <div className="rd-skeleton__cta" />
          <div className="rd-skeleton__icon" />
        </div>
      )}
    </div>
    </div>
  );
}

export default function RequestDetailPage({ requestId: propId, embedded = false }) {
  const params = useParams();
  const id = propId || params.id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slidingOut, setSlidingOut] = useState(false);
  const [interestLoading, setInterestLoading] = useState(false);
  // The map preview stays collapsed until its image arrives; a failed or slow preview left an
  // empty bordered box above Directions.
  const [mapLoaded, setMapLoaded] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  /* Whether the clamped teaser actually cuts text off. Measured, not guessed from length: the
     clamp is two lines on a phone and three from md, and what fits depends on the width. It
     re-measures when the teaser's box changes size (rotation, a pane resize). */
  const descRef = useRef(null);
  const [descClamped, setDescClamped] = useState(false);
  useLayoutEffect(() => {
    const el = descRef.current;
    if (!el || descExpanded) return undefined;
    const measure = () => setDescClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [request?.description, descExpanded, loading]);
  /* Desktop width (>=1024px), where Add to calendar and Share are their own tile; below it they
     are the decision card's foot. Read synchronously on the first render, so the first paint
     already has them in the right place, and kept in step with the viewport. */
  const [wideLayout, setWideLayout] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia?.('(min-width: 1024px)');
    if (!mq) return undefined;
    const sync = () => setWideLayout(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  /* Whether the title fits on one line beside the decision (desktop pane, >=1024px). Only then
     does the decision stand level with the title's first line; if the title would wrap there,
     the decision stays beside the pitch and the title keeps the full width, as before. Measured,
     like the teaser: the title's one-line width (bill chip included) against the pane width
     minus the decision and its 48px gap, with 1px of slack so rounding can never wrap it.
     Resolved in a layout effect, so the first paint is already right; re-measured when the pane
     or the decision changes width and when the web fonts finish loading. */
  const headerMainRef = useRef(null);
  const [titleBeside, setTitleBeside] = useState(false);
  useLayoutEffect(() => {
    const main = headerMainRef.current;
    const title = main?.querySelector('.rd-page-title');
    const action = main?.querySelector('.rd-header-action');
    if (!main || !title || !action) { setTitleBeside(false); return undefined; }
    const measure = () => {
      const was = title.style.whiteSpace;
      title.style.whiteSpace = 'nowrap';
      const range = document.createRange();
      range.selectNodeContents(title);
      const oneLine = range.getBoundingClientRect().width;
      title.style.whiteSpace = was;
      setTitleBeside(oneLine + 1 <= main.clientWidth - action.offsetWidth - 48);
    };
    measure();
    const fonts = document.fonts;
    let live = true;
    fonts?.ready.then(() => { if (live) measure(); });
    fonts?.addEventListener?.('loadingdone', measure);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(main);
    ro?.observe(action);
    return () => { live = false; fonts?.removeEventListener?.('loadingdone', measure); ro?.disconnect(); };
  }, [request, loading, user]);
  /* Whether a contact on this gig has hours to hold against the clock (the clock tick below). */
  const hasContactHours = !!request?.contacts?.some((c) => c.availableHours);
  /* After a successful "I'm Interested" the pill names who received it ("Sent to Muziekcafe De
     Loods") for ~2s, or until the page scrolls, then settles on "Interested". It replaces the
     generic confirmation toast; sentAnnounce keeps the screen-reader announcement the toast gave. */
  const [sentFlash, setSentFlash] = useState(false);
  const [sentAnnounce, setSentAnnounce] = useState('');
  useEffect(() => {
    if (!sentFlash) return undefined;
    const done = () => setSentFlash(false);
    const t = setTimeout(done, 2000);
    window.addEventListener('scroll', done, { passive: true, once: true });
    return () => { clearTimeout(t); window.removeEventListener('scroll', done); };
  }, [sentFlash]);
  const [showTravelPlanning, setShowTravelPlanning] = useState(false);
  const [selectedTransportMode, setSelectedTransportMode] = useState('car');
  const [travelEstimate, setTravelEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showTravelModal, setShowTravelModal] = useState(false);
  const [pendingInterestId, setPendingInterestId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [interests, setInterests] = useState([]);
  const [interestsLoading, setInterestsLoading] = useState(false);

  /* ── Live position on the day's timeline ─────────────────── */
  /* A clock, but only where it can say something true. The timeline belongs to one date, so
     "now" only lands on it on that date — or in the small hours after it, for a night whose
     load-out runs past midnight. On any other day there is nothing to point at, so nothing
     renders and no interval is started: a highlighted "you are here" on a gig three days out
     would be an assertion, not an indicator.
     Thirty seconds is the tick. The finest thing it moves is a gradient stop on a ~40px rail
     segment, so a second-by-second clock would re-render the page for sub-pixel changes.
     It is the page's one clock, read in venue time (venueNow): the live timeline, the countdown,
     the relative day, "gig over" and a contact's hours all read this snapshot. It also runs
     while a contact has hours, to light them up when "now" is inside them. */
  const [venueClock, setVenueClock] = useState(() => venueNow());
  const eventDayDelta = useMemo(
    () => (request?.eventDate ? daysBetween(venueClock.date, request.eventDate) : null),
    [request?.eventDate, venueClock.date],
  );
  const clockIsRelevant = eventDayDelta === 0 || eventDayDelta === -1;
  useEffect(() => {
    if (!clockIsRelevant && !hasContactHours) return undefined;
    const tick = () => setVenueClock((prev) => {
      const next = venueNow();
      return next.date === prev.date && next.minutes === prev.minutes ? prev : next;
    });
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [clockIsRelevant, hasContactHours]);

  /* ── Data loading ────────────────────────────────────────── */

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setMapLoaded(false); loadRequest(); }, [id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user && request?.userHasInterest) loadUserProfile(); }, [user, request?.userHasInterest]);

  const loadRequest = async () => {
    try { setRequest((await getRequest(id)).data); }
    catch { toast.error('Request not found'); navigate('/requests', { replace: true }); }
    finally { setLoading(false); }
  };

  const loadUserProfile = async () => {
    try {
      let r;
      if (user.profileType === 'MUSICIAN') r = await getMyMusicianProfile();
      else if (user.profileType === 'TECH') r = await getMyTechProfile();
      if (r?.data) { setUserProfile(r.data); if (r.data.transportMode) setSelectedTransportMode(r.data.transportMode); }
    } catch (err) { console.error('Failed to load user profile:', err); }
  };

  const loadInterests = async () => {
    if (!user || user.profileType !== 'VENUE') return;
    setInterestsLoading(true);
    try {
      const res = await getRequestInterests(request.id);
      setInterests(res.data || []);
    } catch { setInterests([]); }
    finally { setInterestsLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user?.profileType === 'VENUE' && request) loadInterests(); }, [user, request?.id]);

  /* ── Memoized derived data ─────────────────────────────────── */
  const sortedSetlist = useMemo(() => {
    if (!request?.setlist) return [];
    return [...request.setlist].sort((a, b) => a.order - b.order);
  }, [request?.setlist]);

  /* ── Handlers ────────────────────────────────────────────── */

  const handleToggleInterest = async () => {
    if (!user) { navigate('/login', { state: { from: { pathname: `/requests/${request.reference || request.id}` } } }); return; }
    if (request.userHasInterest) {
      setInterestLoading(true);
      try { await removeInterest(request.id); toast.success('Interest removed'); setShowTravelPlanning(false); setTravelEstimate(null); await loadRequest(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove interest'); }
      finally { setInterestLoading(false); }
      return;
    }
    setShowTravelModal(true);
  };

  const handleTravelConfirm = async (travelData) => {
    setInterestLoading(true);
    try {
      const ir = await addInterest(request.id);
      const iid = ir.data?.interestId;
      // The pill says it was sent and to whom; a toast is kept only where it adds a next step.
      if (iid && travelData.travelMode !== 'not_sure') { await updateInterestTravel(iid, travelData); }
      else if (travelData.travelMode === 'not_sure') { toast.success('Interest added! You can confirm your travel mode later.'); }
      setShowTravelModal(false); await loadRequest();
      const vn = readVenueName(request);
      setSentAnnounce(vn ? `Interest sent to ${vn}` : 'Interest sent');
      setSentFlash(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add interest'); }
    finally { setInterestLoading(false); }
  };

  const handleToggleBookmark = async () => {
    if (!user) { navigate('/login', { state: { from: { pathname: `/requests/${request.reference || request.id}` } } }); return; }
    try { const r = await toggleBookmark(request.id); setRequest(p => ({ ...p, userBookmarked: r.data.bookmarked })); toast.success(r.data.bookmarked ? 'Saved to watchlist' : 'Removed from watchlist'); }
    catch { toast.error('Failed to update bookmark'); }
  };

  const handleEstimateTravel = async () => {
    if (!userProfile?.homeAddress && !userProfile?.city) { toast.error('Set your home address in your profile first'); return; }
    const va = request.venue?.venueAddress || request.venue?.city || request.city;
    if (!va) { toast.error('Venue address not available'); return; }
    setEstimating(true);
    try {
      const ct = request.bookingTerms?.arrivalTime || request.startTime || '18:00';
      const r = await estimateTravel({ origin: userProfile.homeAddress || `${userProfile.city}, Netherlands`, destination: va, mode: selectedTransportMode, departureTime: `${request.eventDate}T${ct}:00` });
      setTravelEstimate(r.data);
    } catch { toast.error('Failed to estimate travel time'); }
    finally { setEstimating(false); }
  };

  const generateGoogleCalendarUrl = (type) => {
    if (!travelEstimate) return null;
    const ed = request.eventDate, ct = request.bookingTerms?.arrivalTime || request.startTime || '18:00', et = request.bookingTerms?.endTime || request.endTime || '23:00';
    const vn = readVenueName(request) || 'Venue', va = request.venue?.venueAddress || request.venue?.city || request.city;
    const role = request.roleSubtype || (request.roleTypeNeeded === 'MUSICIAN' ? 'Musician' : 'Tech'), rate = request.budgetAmount || 0;
    const cd = new Date(`${ed}T${ct}:00`), ced = new Date(`${ed}T${et}:00`);
    const ts = new Date(cd.getTime() - travelEstimate.durationMinutes * 60000);
    const fg = d => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    if (type === 'travel') return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`Travel to ${vn}`)}&dates=${fg(ts)}/${fg(cd)}&details=${encodeURIComponent(`Mode: ${selectedTransportMode}\nDuration: ${travelEstimate.durationMinutes} min`)}&location=${encodeURIComponent(va)}`;
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`STAGE8 – ${role} @ ${vn}`)}&dates=${fg(cd)}/${fg(ced)}&details=${encodeURIComponent(`Call: ${ct}\nRate: €${rate}\nTravel: ${travelEstimate.durationMinutes} min`)}&location=${encodeURIComponent(va)}`;
  };

  const handleGoogleCalendar = () => {
    if (!travelEstimate) { toast.error('Estimate travel first'); return; }
    const t = generateGoogleCalendarUrl('travel'), g = generateGoogleCalendarUrl('gig');
    if (t) window.open(t, '_blank');
    setTimeout(() => { if (g) window.open(g, '_blank'); }, 500);
    toast.success('Opening Google Calendar');
  };

  const handleAppleCalendar = async () => {
    if (!travelEstimate) { toast.error('Estimate travel first'); return; }
    setDownloading(true);
    try {
      const ed = request.eventDate, ct = request.bookingTerms?.arrivalTime || request.startTime || '18:00', et = request.bookingTerms?.endTime || request.endTime || '23:00';
      const r = await downloadBothICS({ venueName: readVenueName(request) || 'Venue', venueAddress: request.venue?.venueAddress || request.city, role: request.roleSubtype || 'Musician', callTime: `${ed}T${ct}:00`, endTime: `${ed}T${et}:00`, rate: request.budgetAmount || 0, transportMode: selectedTransportMode, travelMinutes: travelEstimate.durationMinutes });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/calendar' }));
      const a = document.createElement('a'); a.href = url; a.download = `stage8-gig.ics`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Calendar file downloaded');
    } catch { toast.error('Failed to create calendar events'); }
    finally { setDownloading(false); }
  };

  const handleExportCalendar = () => {
    // ── STAGE8 .ics export with reference code prominent ─────────────────
    // RFC 5545 — CRLF line separators, 75-octet line folding, text escaping.
    const ref = (request.reference || `S8-${(request.id || '').slice(0, 4).toUpperCase()}-${(request.id || '').slice(-4).toUpperCase()}`).toUpperCase();
    const ed = request.eventDate;
    const st = request.startTime || '18:00';
    const en = request.endTime || '23:00';
    const title = request.title || 'Gig';
    const vn = readVenueName(request) || 'Venue';
    const va = request.venueAddress || request.venue?.venueAddress || '';
    const city = request.city || '';
    const desc = request.description || '';
    const fee = request.budgetAmount || 0;
    const attendance = request.expectedAttendance;
    const callTime = request.timeline?.callTime || st;

    const ymd = ed.replace(/-/g, '');
    const dtStart = `${ymd}T${st.replace(':', '')}00`;
    const dtEnd = `${ymd}T${en.replace(':', '')}00`;
    const dtStamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const feeStr = `€${formatCount(fee)} ${getRateTypeLabel(request.rateType).toLowerCase()}`;

    // RFC 5545 text escape: backslash, semicolon, comma, newline
    const esc = (s) => String(s ?? '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');

    // Fold a single logical line at 75 octets (UTF-8 byte-aware), continuation starts with SPACE.
    const foldLine = (line) => {
      const enc = new TextEncoder();
      const bytes = enc.encode(line);
      if (bytes.length <= 75) return line;
      const chunks = [];
      let start = 0;
      // first chunk: up to 75 bytes
      const sliceBytes = (from, max) => {
        // walk chars, counting bytes, until we'd exceed `max`
        let consumed = 0;
        let i = from;
        // operate on code units via substring, re-encoding each candidate is O(n) but fine for our size
        let lastGood = from;
        while (i < line.length) {
          // UTF-16 surrogate pair awareness: take one code point at a time
          const cp = line.codePointAt(i);
          const ch = String.fromCodePoint(cp);
          const b = enc.encode(ch).length;
          if (consumed + b > max) break;
          consumed += b;
          i += ch.length;
          lastGood = i;
        }
        return [line.substring(from, lastGood), lastGood];
      };
      let [chunk, next] = sliceBytes(0, 75);
      chunks.push(chunk);
      start = next;
      while (start < line.length) {
        // continuation lines can carry up to 74 bytes after the leading space
        [chunk, next] = sliceBytes(start, 74);
        chunks.push(' ' + chunk);
        start = next;
      }
      return chunks.join('\r\n');
    };

    const locationRaw = [vn, va, city].filter(Boolean).join(', ');
    const summaryRaw = `${title} · ${vn}`;
    const url = getShareUrl();

    const descriptionRaw = [
      `STAGE8 Reference: ${ref}`,
      '',
      desc,
      '',
      '─────────────',
      `Call Time:    ${callTime}`,
      `Show:         ${st} - ${en}`,
      `Fee:          ${feeStr}`,
      `Attendance:   ${attendance != null ? `${attendance} expected` : '—'}`,
      '',
      `Venue:        ${vn}`,
      `Address:      ${va || city}`,
      '',
      'Open on STAGE8:',
      url,
    ].join('\n');

    const rawLines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//STAGE8//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${ref}@stage8.io`,
      `DTSTAMP:${dtStamp}`,
      `DTSTART;TZID=Europe/Amsterdam:${dtStart}`,
      `DTEND;TZID=Europe/Amsterdam:${dtEnd}`,
      `SUMMARY:${esc(summaryRaw)}`,
      `LOCATION:${esc(locationRaw)}`,
      `URL:${url}`,
      `DESCRIPTION:${esc(descriptionRaw)}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ];
    const ics = rawLines.map(foldLine).join('\r\n') + '\r\n';

    // Blob → download, filename = {REFERENCE}.ics
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `${ref}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
    toast.success(`Calendar event downloaded · ${ref}`);
  };

  const isOwner = !!request && user?.profileType === 'VENUE' && !!user.profileId
    && (request.creatorId === user.profileId || request.venueProfileId === user.profileId);

  const canShowInterest = () => {
    if (!user) return true;
    if (user.profileType === 'VENUE') return false;
    if (request.roleTypeNeeded === 'MUSICIAN' && user.profileType !== 'MUSICIAN') return false;
    if (request.roleTypeNeeded === 'TECH' && user.profileType !== 'TECH') return false;
    return true;
  };

  const handleBack = () => {
    if (embedded) return;
    // With reduced motion there is no exit animation to wait for, so Back is immediate.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { navigate(-1); return; }
    setSlidingOut(true);
    // Matches .drill-slide-out's 200ms.
    setTimeout(() => navigate(-1), 200);
  };

  const getShareUrl = () => {
    if (!request) return '';
    const slug = request.slug || toSlug(request.title, readVenueName(request), request.city, request.eventDate);
    return `${window.location.origin}/gig/${slug}`;
  };

  /* The Clipboard API only exists in a secure context. Over plain http — the dev server
     opened from a phone on the LAN — it is undefined, so fall back to a selected textarea. */
  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return; }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    if (!ok) throw new Error('copy failed');
  };

  const handleShare = async () => {
    const url = getShareUrl();
    const title = (request.title || '').replace('[DEMO] ', '');
    const text = `${title} — ${readVenueName(request)}, ${request.city}`;
    // Returns what happened, for the key to say: 'Shared', 'Link copied', or nothing.
    // Phones and tablets (touch, no hover) get the system share sheet, to pick an app; with a
    // mouse or trackpad the link is copied, even where the browser has a share sheet (desktop
    // Safari, Chrome on Windows). The sheet only exists on a secure (https) page.
    const touch = !!window.matchMedia?.('(hover: none) and (pointer: coarse)').matches;
    if (touch && navigator.share) { try { await navigator.share({ title: `STAGE8 — ${title}`, text, url }); return 'Shared'; } catch (e) { if (e.name === 'AbortError') return null; } }
    try { await copyText(url); toast.success('Link copied'); return 'Link copied'; }
    catch { toast.error('Failed to copy link'); return null; }
  };

  /* ── Render guards ───────────────────────────────────────── */

  /* Loading renders the same shell as the loaded page — same root, same animated box — so
     React reuses both nodes and the drill animation starts the moment the screen mounts
     rather than after the fetch returns. Measured at 4x CPU throttle, the old gate put the
     animation entirely after the wait: skeleton at 307ms, content at 515ms, movement not
     finished until 949ms. Starting it here means the slide covers the fetch instead of
     queueing behind it.
     The blocks are shaped like what replaces them — action bar, title, venue line, cards at
     the tile width — so the swap fills in rather than jumps. */
  if (loading) return <RequestDetailSkeleton embedded={embedded} />;
  if (!request) return null;

  const isExpired = request.status === 'EXPIRED';
  const bt = request.bookingTerms || {};
  const tl = request.timeline || {};
  const hosp = request.hospitality || {};
  const equip = request.equipment || {};
  const env = request.environment || {};
  // null (not {}) when the venue stated no catering — the facilities rows key on that
  const catering = request.catering || null;
  const urg = urgencyDisplay(request.urgency);
  const venueName = readVenueName(request);
  const showTransitWarning = selectedTransportMode === 'public' && request.venue?.publicTransitAccess === 'poor';

  /* Check-type logic — determine which pre-show check(s) render, and which applies to THIS musician's bill_position */
  const checkType = tl.checkType || 'soundcheck';
  const billPosition = request.roleExecution?.billPosition;
  const musicianGetsSoundcheck = ['solo_bill', 'headliner'].includes(billPosition);

  /* Timeline computations — presence starts at the call time: the venue sets it on this
     musician's request and the timeline shows it as their arrival. The applicable check
     start is only the fallback when no call time is set. */
  const presenceStart = (() => {
    if (tl.callTime) return tl.callTime;
    if (checkType === 'none') return tl.callTime;
    if (checkType === 'soundcheck') return tl.soundcheckStart || tl.callTime;
    if (checkType === 'line_check') return tl.lineCheckStart || tl.callTime;
    if (checkType === 'both') return (musicianGetsSoundcheck ? tl.soundcheckStart : tl.lineCheckStart) || tl.callTime;
    return tl.callTime;
  })();
  const totalPresence = calcDuration(presenceStart, tl.loadOutBy || tl.curfew || tl.setEnd);
  const showTime = calcDuration(tl.setStart, tl.setEnd);
  const downtime = totalPresence != null && showTime != null ? totalPresence - showTime : null;

  /* Equipment counts */
  const venueGear = equip.venueProvides || [];
  const artistGear = equip.artistBrings || [];

  /* Setlist summary — "10 tracks · 45m · 8 originals"; originals counted from the ORIGINAL tag (artist === 'Original') */
  const SETLIST_PREVIEW_COUNT = 3;
  const setlistTotalSec = sortedSetlist.reduce((sum, s) => {
    if (!s.duration) return sum;
    const [m, sec] = s.duration.split(':').map(Number);
    return sum + m * 60 + (sec || 0);
  }, 0);
  /* One definition of "cover" for the whole card: a named artist that is not the literal
     "Original". The row badge and the summary both read from it, so they can never disagree. */
  const isCoverTrack = (song) => !!song.artist && song.artist !== 'Original';
  const setlistCovers = sortedSetlist.filter(isCoverTrack).length;
  const setlistOriginals = sortedSetlist.length - setlistCovers;
  /* Badge the informative minority, never the majority: a COVER tag on all 30 of 30 tracks is
     noise, not information. When one class is in the minority its rows are badged; when the
     list is all one thing nothing is badged and the summary says so once. */
  const setlistBadged =
    setlistCovers === 0 || setlistOriginals === 0 ? null
    : setlistCovers <= setlistOriginals ? 'cover' : 'original';
  /* Each part is { num, word }. Mono means time on this page, so only the running time (time:
     true) takes it; the counts are body figures like the words beside them
     ("10 tracks · 45m · 9 originals"). */
  const setlistSummary = [
    { num: sortedSetlist.length, word: sortedSetlist.length === 1 ? 'track' : 'tracks' },
    setlistTotalSec > 0 && (() => {
      const h = Math.floor(setlistTotalSec / 3600);
      const m = Math.floor((setlistTotalSec % 3600) / 60);
      return { num: h > 0 ? `${h}h ${m}m` : `${m}m`, time: true };
    })(),
    setlistCovers === 0 && sortedSetlist.length > 0 && { word: 'all originals' },
    setlistOriginals === 0 && sortedSetlist.length > 0 && { word: 'all covers' },
    setlistOriginals > 0 && setlistCovers > 0 && { num: setlistOriginals, word: setlistOriginals === 1 ? 'original' : 'originals' },
  ].filter(Boolean);
  /* The caption size, like every summary and hint line on the page. */
  const setlistHeaderRow = <SummaryLine parts={setlistSummary} className="mb-3" data-testid="setlist-summary" />;
  /* Timeline events array */
  const doorsAlreadyOpen = tl.doorsAlreadyOpen === true || request.doorsAlreadyOpen === true;
  const loadOut = computeLoadOut(tl, bt);

  // Build check event rows based on checkType + billPosition
  // musician's own check (used for presence calc, not rendered differently)
  const checkEvents = [];
  /* `span` is how long the activity itself lasts, from its start to its own end time. Only
     rows with a real end carry one; the end time is said by the duration, not in the label. */
  const scSpan = tl.soundcheckStart && tl.soundcheckEnd ? calcDuration(tl.soundcheckStart, tl.soundcheckEnd) : null;
  const lcSpan = tl.lineCheckStart && tl.lineCheckEnd ? calcDuration(tl.lineCheckStart, tl.lineCheckEnd) : null;
  if (checkType === 'soundcheck' && tl.soundcheckStart) {
    checkEvents.push({ time: tl.soundcheckStart, label: 'Soundcheck', span: scSpan, priority: 'normal', variant: 'time' });
  } else if (checkType === 'line_check' && tl.lineCheckStart) {
    checkEvents.push({ time: tl.lineCheckStart, label: 'Line Check', span: lcSpan, priority: 'normal', variant: 'time' });
  } else if (checkType === 'both') {
    // Render BOTH; emphasize the one that applies to this musician's bill position
    // "Your soundcheck" rather than "Soundcheck (your check)": shorter, so it holds one line
    // at phone width, and it says whose check it is first.
    checkEvents.push({
      time: tl.soundcheckStart,
      // Whose check it is rides as a muted sub-line under the label.
      label: musicianGetsSoundcheck ? 'Your Soundcheck' : 'Soundcheck',
      suffix: musicianGetsSoundcheck ? null : 'headliner',
      span: scSpan,
      priority: musicianGetsSoundcheck ? 'normal' : 'muted',
      variant: 'time',
    });
    checkEvents.push({
      time: tl.lineCheckStart,
      label: musicianGetsSoundcheck ? 'Line Check' : 'Your Line Check',
      suffix: musicianGetsSoundcheck ? 'supports' : null,
      span: lcSpan,
      priority: musicianGetsSoundcheck ? 'muted' : 'normal',
      variant: 'time',
    });
  }

  /* The hard stop is the curfew's own property when it is "Fixed" (today's form) or when an older
     request stored it as the curfew's time with no reason attached ("00:00"). Then it is said
     on the curfew row and the separate chip is dropped. A hard stop with its own reason
     ("21:10 — next act at 21:30") says something the row does not, so it keeps the chip. */
  const curfewIsHardStop = !!tl.curfew && (bt.hardStop === 'Fixed' || String(bt.hardStop || '').trim() === tl.curfew);

  const timelineEvents = (() => {
    const base = [
      tl.callTime && { time: tl.callTime, label: 'Call Time / Arrival', priority: 'normal', variant: 'time' },
      doorsAlreadyOpen
        ? { time: null, label: 'Doors Already Open', priority: 'muted', variant: 'status' }
        : tl.doorsOpen && tl.doorsOpen !== 'Already open' && { time: tl.doorsOpen, label: 'Doors Open', priority: 'normal', variant: 'time' },
      tl.setStart && { time: tl.setStart, label: 'Show Start', span: tl.setEnd ? calcDuration(tl.setStart, tl.setEnd) : null, priority: 'key', variant: 'time' },
      tl.setEnd && { time: tl.setEnd, label: 'Show End', priority: 'key', variant: 'time' },
      // A fixed hard stop is the curfew's own property, so it is said on that row rather than
      // repeated as a separate red chip under the timeline.
      tl.curfew && { time: tl.curfew, label: curfewIsHardStop ? 'Curfew (Hard Stop)' : 'Curfew', priority: 'red', variant: 'time' },
      loadOut && (loadOut.type === 'warning'
        ? { time: null, label: loadOut.label, subtext: loadOut.subtext, priority: 'red', variant: 'warning' }
        : { time: loadOut.value, label: 'Load-Out Complete', priority: 'muted', variant: 'time' }),
    ].filter(Boolean);

    // Insert check events chronologically BEFORE doorsOpen (or before setStart if doors absent).
    // Timeline base list is pre-ordered logically; inserting check rows before the public-facing events keeps curfew/load-out after setEnd without midnight-wrap issues.
    if (checkEvents.length === 0) return base;
    const anchorIdx = base.findIndex(e => e.label === 'Doors Open' || e.label === 'Doors Already Open' || e.label === 'Show Start');
    const insertAt = anchorIdx >= 0 ? anchorIdx : 1; // after callTime if no doors/show anchor
    // Sort check events chronologically among themselves
    const sortedChecks = [...checkEvents].sort((a, b) => {
      const toMin = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + (m || 0); };
      return toMin(a.time) - toMin(b.time);
    });
    // Additionally — if Doors Open time is BETWEEN two check rows, split the inserts
    const doorsRow = base[anchorIdx];
    if (doorsRow && doorsRow.time && sortedChecks.length === 2) {
      const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
      const doorMin = toMin(doorsRow.time);
      const c0Min = toMin(sortedChecks[0].time);
      const c1Min = toMin(sortedChecks[1].time);
      if (c0Min <= doorMin && doorMin <= c1Min) {
        // Insert first check BEFORE doors, second check AFTER doors
        return [...base.slice(0, anchorIdx), sortedChecks[0], doorsRow, sortedChecks[1], ...base.slice(anchorIdx + 1)];
      }
    }
    return [...base.slice(0, insertAt), ...sortedChecks, ...base.slice(insertAt)];
  })();

  /* ══════════════════════════════════════════════════════════ */
  /* ═══  JSX — 12-ZONE LAYOUT (spec v2)  ═══════════════════ */
  /* ══════════════════════════════════════════════════════════ */

  // ZONE 10 contacts card
  /* The bar is built once and mounted in one of two places, never both — one DOM node, so
     the testids and the aria state stay unique. Standalone (the phone) it is the docked
     bar at the foot of the screen. Embedded (the desktop split pane) it rides at the head
     of the venue column, directly above On-Site Contacts. The Back control is inside the
     bar's own !embedded test, so the desktop copy carries only the decision and save. */
  const actionBar = (
    <div className={cn('rd-action-bar', !embedded && 'rd-action-bar--docked')} data-testid="desktop-action-panel">
      {!embedded && (
        <button
          type="button"
          onClick={handleBack}
          aria-label="Back"
          className="rd-action-bar__icon rd-key hover:text-[var(--t1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--role-money)]"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      )}
      {/* The venue that posted the request gets its own primary action. Same condition the
          Manage Requests list uses for its Edit link: only while the request is open. */}
      {isOwner && request.status === 'OPEN' && (
        <Link
          to={`/venues/requests/${request.id}/edit`}
          className="rd-action-bar__primary rd-key rd-key--amber flex items-center justify-center gap-2 text-sm font-bold uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--role-money)]"
          style={{ fontFamily: 'var(--s8-font-condensed)', letterSpacing: '0.1em', color: 'var(--bg)', borderRadius: 'var(--s8-radius-pill)' }}
          data-testid="edit-request-btn"
        >
          <SquarePen className="h-4 w-4" aria-hidden /> Edit request
        </Link>
      )}
      {canShowInterest() && !isExpired && (<>
        <button type="button" onClick={handleToggleInterest} disabled={interestLoading}
          className="rd-action-bar__primary rd-key rd-key--amber flex items-center justify-center gap-2 text-sm font-bold uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--role-money)]"
          /* Once sent, the pill says what is true ("Interested", green = confirmed) rather than
             offering the undo as the loudest thing on screen. Tapping it still withdraws; the
             accessible name says so, and aria-pressed carries the state. */
          aria-pressed={request.userHasInterest ? 'true' : 'false'}
          data-sent={request.userHasInterest && sentFlash && venueName ? 'true' : undefined}
          aria-label={request.userHasInterest ? `${sentFlash && venueName ? `Sent to ${venueName}` : 'Interested'}. Tap to withdraw interest` : undefined}
          style={{ fontFamily: 'var(--s8-font-condensed)', letterSpacing: '0.1em', color: request.userHasInterest ? 'var(--role-covered)' : 'var(--bg)', opacity: interestLoading ? 0.6 : 1, borderRadius: 'var(--s8-radius-pill)' }}
          data-testid="interest-btn-desktop"
        >
          {/* One width in every state, so the dock around it never changes size and Back and Save
              never move: an invisible copy of the widest label ("I'm Interested") sizes the pill,
              and the label actually shown sits on top of it without adding width of its own. */}
          <span className="rd-pill-stack">
            <span className="rd-pill-sizer" aria-hidden><Heart className="h-4 w-4" /> I'm Interested</span>
            <span className="rd-pill-live">
              {request.userHasInterest && sentFlash && venueName ? (
                /* Two short lines, in normal case, so a venue name fits the pill at 320px; the swap is
                   instant (no transition), so reduced motion needs no separate path. */
                <span className="flex flex-col items-start normal-case min-w-0" style={{ letterSpacing: '0.02em', lineHeight: 1.15 }} data-testid="interest-sent">
                  <span style={{ fontSize: 'var(--s8-fs-label)', fontWeight: 500 }}>Sent to</span>
                  <span className="max-w-full truncate">{venueName}</span>
                </span>
              ) : request.userHasInterest ? <><Heart className="h-4 w-4" style={{ fill: 'currentColor' }} /> Interested</> : <><Heart className="h-4 w-4" /> I'm Interested</>}
            </span>
          </span>
        </button>
        <span className="sr-only" aria-live="polite">{sentAnnounce}</span>
        {/* Icon only, so the name it answers to is the aria-label. aria-pressed carries the
            saved state that the label used to spell out. */}
        <button type="button" onClick={handleToggleBookmark}
          aria-label={request.userBookmarked ? 'Saved for later' : 'Save for later'}
          aria-pressed={request.userBookmarked ? 'true' : 'false'}
          className="rd-action-bar__icon rd-key hover:text-[var(--t1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--role-money)]"
          data-testid="bookmark-btn-desktop"
        >
          <Bookmark className="h-[18px] w-[18px]" style={{ fill: request.userBookmarked ? 'currentColor' : 'none' }} />
        </button>
      </>)}
    </div>
  );

  /* Add to calendar and Share, two stage keys. Mounted once: as their own tile from 1024px, as
     their own row under the terms below it (wideLayout), where they are drawn as the outlined
     pair with the calendar-plus and share-out glyphs. Only while there is a date and the gig is
     open. */
  const utilityKeys = request.eventDate && !isExpired ? (
    <>
      <UtilityKey
        glyph={wideLayout ? <DateLeaf day={Number(request.eventDate.slice(8, 10))} /> : <CalendarPlus width={18} height={18} strokeWidth={1.78} aria-hidden />}
        label="Add to Calendar"
        onPress={() => { handleExportCalendar(); return 'Downloaded'; }}
        testId="calendar-export-btn"
      />
      <UtilityKey
        glyph={wideLayout ? <Share2 width={18} height={18} strokeWidth={1.78} aria-hidden /> : <Share width={18} height={18} strokeWidth={1.78} aria-hidden />}
        label="Share"
        onPress={handleShare}
        testId="share-gig-btn"
      />
    </>
  ) : null;

  /* A tile wraps a card that exists. A section with nothing to show renders no wrapper, so it
     leaves no padding or gap behind: an empty wrapper still carried the column gap, which opened
     a hole on a sparse gig (120px on a phone) and dropped the next column's head. */
  const tile = (card) => (card ? <div className="rd-tile">{card}</div> : null);

  /* A render function, not a value: whether the gig is over reads `countdown`, which is only
     worked out further down this component. Called from the JSX, after every value exists. */
  const renderContactsCard = () => (request.contacts && request.contacts.length > 0) ? (
    <Section tier="standard">
      <SectionHeader icon={Phone}>On-Site Contacts</SectionHeader>
      {/* One row per contact, shared dividers (no nested pockets). Hierarchy: name → role →
          number, then the hours in mono (times are the mono's one job). The whole row is the call target; amber is reserved
          for the call button so the one action reads first. Press feedback on pointer-down.

          The call target is 48px, not 52. The docked action bar holds its controls at 52
          (--rd-action-size) specifically so it outranks the in-card controls, so matching it
          here would flatten the two into one size. 48 is as large as this can go while the
          bar still reads as the louder of the two. The glyph goes with it — 22px keeps the
          same share of the circle it had at 44/20, rather than sitting marooned in a bigger
          target. Row height is unaffected: the name/number block is ~77px, taller than either. */}
      <div className="divide-y divide-[color:var(--s8-b1-solid)]">
        {request.contacts.map((c, i) => {
          const hours = c.availableHours && String(c.availableHours).replace(/\s*[-–]\s*/, '–');
          /* Call state, from the contact's hours and the venue clock (venueClock), on any
             day until the gig is over:
             'now'  inside the hours: button traced in --role-covered, "Available now · until 00:00";
             'off'  outside the hours, or the gig is over: the button goes quiet (muted icon),
                    and once the gig is over the hours line reads "Gig over";
             null   no hours given: unknown is not "off", so the button stays in its plain state.
             Calling is always possible; the state only says whether now is a good moment. */
          const availableNow = !gigOver && !!hours && inTimeWindow(hours, venueClock.minutes);
          const callState = availableNow ? 'now' : hours ? 'off' : null;
          const body = (
            <>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <span style={{ fontSize: 'var(--s8-fs-lead)', fontWeight: 600, lineHeight: 1.25, color: 'var(--t0)' }}>{c.name}</span>
                  {c.role && <span style={{ fontSize: 'var(--s8-fs-caption)', lineHeight: 1.25, color: 'var(--t2)' }}>{c.role}</span>}
                  {c.isEmergency && (
                    <span className="shrink-0 inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest border text-[var(--role-limit)] bg-[var(--role-limit)]/10 border-[var(--role-limit)]/25" style={{ borderRadius: 'var(--s8-radius-xs)' }}>
                      Emergency
                    </span>
                  )}
                </span>
                {(hours || c.phone) && (
                  <span className="block mt-2.5" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: 'var(--s8-fs-caption)', lineHeight: 1.65 }}>
                    {c.phone && <span className="block" style={{ fontFamily: 'var(--s8-font-body)', fontVariantNumeric: 'tabular-nums', wordSpacing: '0.15em', color: 'var(--t0)' }} data-testid={`contact-phone-${i}`}>{c.phone}</span>}
                    {hours && (
                      <span className="block" style={{ color: 'var(--t3)' }} data-testid={`contact-hours-${i}`} data-available-now={availableNow ? 'true' : undefined}>
                        {gigOver ? (
                          /* Once the gig is over the hours no longer apply, so the line says so
                             instead of listing them. The call stays possible (pay, forgotten gear). */
                          <span style={{ fontFamily: 'var(--s8-font-body)', color: 'var(--t2)' }}>Gig over</span>
                        ) : availableNow ? (
                          /* Inside the hours the start has passed, so only the end is news: the
                             words say it is a good moment to call, not only the colour. */
                          <>
                            <span style={{ fontFamily: 'var(--s8-font-body)', color: 'var(--role-covered)' }}>Available now</span>
                            <span style={{ color: 'var(--t2)', whiteSpace: 'nowrap' }}>
                              <span style={{ fontFamily: 'var(--s8-font-body)' }}> · until </span>{hours.split('–')[1]}
                            </span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontFamily: 'var(--s8-font-body)' }}>Available</span>{' '}
                            <span style={{ color: 'var(--t1)' }}>
                              {hours.includes('–') ? <TimeRange from={hours.split('–')[0]} to={hours.split('–')[1]} /> : hours}
                            </span>
                          </>
                        )}
                      </span>
                    )}
                  </span>
                )}
              </span>
              {c.phone && (
                <span
                  aria-hidden
                  className="rd-call-btn rd-key shrink-0 flex items-center justify-center"
                  /* A raised, pressable disc (.rd-call-btn). Its colour is the call state:
                     traced in --role-covered inside the hours, muted outside them or once the gig
                     is over, plain when the contact gave no hours. */
                  style={{ width: '48px', height: '48px', borderRadius: 'var(--s8-radius-pill)' }}
                  data-call-state={callState || undefined}
                  data-call-now={availableNow ? 'true' : undefined}
                >
                  <Phone style={{ width: '22px', height: '22px' }} />
                </span>
              )}
            </>
          );
          // py-5 gives the rows their rhythm, but at the list's outer edges it stacked on
          // top of the card's own 20px padding — 40px under the header and above the card
          // floor. Zeroed at the ends only, so row-to-row spacing is untouched.
          const rowClass = 'flex items-center gap-5 py-5 first:pt-0 last:pb-0';
          const key = c.name ? `${c.name}-${c.role}` : `contact-${i}`;
          return c.phone ? (
            <a
              key={key}
              href={`tel:${String(c.phone).replace(/[\s-]/g, '')}`}
              className={cn(rowClass, 'group outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--role-money)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--s8-bg-card)]')}
              style={{ borderRadius: 'var(--s8-radius-sm)' }}
              /* The state is said, not only shown: "available now" / "outside hours" (or "gig
                 over" once it is, when the hours no longer apply). */
              aria-label={`Call ${c.name || c.phone}${c.role ? `, ${c.role}` : ''}${
                !hours ? ''
                  : availableNow ? `, available now, hours ${hours}`
                  : gigOver ? ', gig over'
                  : `, outside hours, available ${hours}`}`}
              data-testid={`contact-card-${i}`}
            >
              {body}
            </a>
          ) : (
            <div key={key} className={rowClass} data-testid={`contact-card-${i}`}>{body}</div>
          );
        })}
      </div>
    </Section>
  ) : null;

  // ZONE 9 logistics card — one fact list; labels say what the data actually is
  // (includedRadiusKm is the radius travel is covered within, not the distance to the venue)
  const PARKING_TYPE_LABEL = { onsite: 'On-site', street: 'Street', garage: 'Garage', none: 'None', other: 'Other' };
  const logisticsCard = (
    <Section tier="standard">
      <SectionHeader icon={MapPin}>Logistics</SectionHeader>

      {/* Your Travel — personalised travel time (logged-in users only) */}
      {SHOW_TRAVEL_TIME_PANEL && user?.profileLat && user?.profileLng && request.venue_lat && request.venue_lng && (
        <TravelTimePanel
          fromLat={user.profileLat}
          fromLng={user.profileLng}
          toLat={request.venue_lat}
          toLng={request.venue_lng}
        />
      )}

      {/* Row labels one step up the text ladder (--t1), so the question reads as clearly as
          its answer on a phone in bad light; values keep their own colours. Labels, values and
          the Directions row are set at the card-row size the Equipment rows use (18px on 1.35).
          No pull-up: the list starts the same 16px under the title as every other card's body.
          One-line rows are open, on Venue Facilities' pitch; only the multi-line notes are ruled
          off (.rd-logistics-facts in index.css). */}
      <div className={cn('rd-logistics-facts', !(request.venue_lat && request.venue_lng) && 'rd-logistics-facts--end')} data-testid="logistics-facts">
        <FactRow lead labelColor="var(--t1)" label="Travel Covered Within" tone={bt.includedRadiusKm ? undefined : 'muted'}>
          {bt.includedRadiusKm ? `${bt.includedRadiusKm} km` : <NotGiven />}
        </FactRow>
        {bt.beyondRadiusRate > 0 && bt.includedRadiusKm && (
          <FactRow lead labelColor="var(--t1)" label={`Beyond ${bt.includedRadiusKm} km`} tone="money">€{formatCents(bt.beyondRadiusRate)}/km</FactRow>
        )}
        <FactRow lead labelColor="var(--t1)" label="Parking" tone={request.parkingType ? undefined : 'muted'}>
          {PARKING_TYPE_LABEL[request.parkingType] || <NotGiven />}
        </FactRow>
        {request.estimatedParkingCost != null && (
          <FactRow
            lead
            label="Parking Cost"
            tone={request.estimatedParkingCost === 0 ? 'ok' : 'money'}
            sub={request.parkingCostBasis ? request.parkingCostBasis.split('—')[0].trim() : null}
          >
            {request.estimatedParkingCost === 0 ? 'Free' : `€${formatCents(request.estimatedParkingCost)}`}
          </FactRow>
        )}
        {bt.parkingCovered != null && (
          <FactRow lead labelColor="var(--t1)" label="Parking Cost" tone={bt.parkingCovered ? 'ok' : 'limit'}>{bt.parkingCovered ? 'Covered by venue' : 'Not covered'}</FactRow>
        )}
        {request.nearestParking && (
          <FactRow lead labelColor="var(--t1)" label="Nearest Parking" stacked>
            {request.nearestParking}
            {request.nearestParkingWalk && <span style={{ color: 'var(--t3)' }}> · {request.nearestParkingWalk}</span>}
          </FactRow>
        )}
        {request.parkingNote && (
          <FactRow lead labelColor="var(--t1)" label="Parking Details" stacked><span className="whitespace-pre-line">{request.parkingNote}</span></FactRow>
        )}
        {request.loadingZone && (
          <FactRow lead labelColor="var(--t1)" label="Loading Zone" stacked={!!request.loadingZoneNote} tone={request.loadingZoneNote ? undefined : 'ok'}>
            {request.loadingZoneNote || 'Available'}
          </FactRow>
        )}
        {bt.publicTransitNotes && (
          <FactRow lead labelColor="var(--t1)" label="Public Transport" stacked>{bt.publicTransitNotes}</FactRow>
        )}
        {bt.tollsCovered != null && (
          <FactRow lead labelColor="var(--t1)" label="Tolls" tone={bt.tollsCovered ? 'ok' : 'limit'}>{bt.tollsCovered ? 'Covered' : 'Not covered'}</FactRow>
        )}
      </div>

      {/* Directions — one control: map preview (when it loads) + labelled row. No map → no link. */}
      {request.venue_lat && request.venue_lng && (
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${request.venue_lat},${request.venue_lng}`}
          target="_blank"
          rel="noopener noreferrer"
          className={cn('group block outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--role-money)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--s8-bg-card)]', mapLoaded && 'mt-2')}
          style={{ borderRadius: 'var(--s8-radius-md)' }}
          data-testid="venue-map-link"
        >
          <div className="venue-map-wrapper" style={mapLoaded ? undefined : { height: 0, margin: 0, border: 0 }}>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/map-preview?lat=${request.venue_lat}&lng=${request.venue_lng}`}
              alt=""
              loading="lazy"
              className="venue-map-image"
              onLoad={() => setMapLoaded(true)}
              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
              data-testid="venue-map-image"
            />
          </div>
          <span
            className="flex items-center justify-between gap-3 py-2.5 transition-colors text-[color:var(--t1)] group-hover:text-[color:var(--t0)]"
            /* With no map above it, the row joins the fact list: its rule is one more divider
               at the list's own spacing, not a rule with a gap stacked over it. The fact rows'
               10px above and below, so a row that takes two lines keeps the same air. */
            /* The last thing on the card: the 44px target runs 10px into the card's padding, so
               the text ends as far from the bottom edge as the other cards' last lines. */
            style={{ minHeight: '44px', borderTop: '1px solid var(--s8-b1-solid)', marginTop: mapLoaded ? '8px' : 0, marginBottom: '-10px' }}
          >
            {/* The venue name is never cut: where it does not fit beside "Directions" at the row
                size, it moves to its own line under it. */}
            <span className="flex flex-wrap items-center gap-x-2 min-w-0" style={{ fontSize: 'var(--s8-fs-lead)', lineHeight: 1.35 }}>
              <Navigation className="h-4 w-4 shrink-0" style={{ color: 'var(--t2)' }} aria-hidden />
              <span>Directions</span>
              {venueName && <span className="min-w-0" style={{ color: 'var(--t3)' }}>{venueName}</span>}
            </span>
            <ArrowUpRight className="h-4 w-4 shrink-0" style={{ color: 'var(--t2)' }} aria-hidden />
          </span>
        </a>
      )}
    </Section>
  );

  // ── Day-timeline intervals ────────────────────────────────
  // Vertical rhythm stays compact; the empty space to the RIGHT of each label
  // does the work. A row with its own end time carries its duration as a right-
  // aligned duration, tied to the label by a dotted leader that stretches to
  // fill whatever horizontal room the row has.
  /* Clock times unrolled onto one continuous scale, so a load-out at 00:00 reads as later
     than a curfew at 23:30 rather than twenty-three hours earlier. Any step backwards means
     the day has crossed midnight. */
  const timelineAbs = (() => {
    let carry = 0, prev = null;
    return timelineEvents.map((ev) => {
      const m = timeToMinutes(ev.time);
      if (m === null) return null;
      if (prev !== null && m < prev) carry += 1440;
      prev = m;
      return m + carry;
    });
  })();

  /* Where the clock sits on that scale, and which row owns it. nowAbs takes the same +1440
     when we are in the small hours of the morning after. */
  const livePosition = (() => {
    if (!clockIsRelevant) return null;
    const stamps = timelineAbs.filter((m) => m !== null);
    if (stamps.length < 2) return null;
    const startAbs = stamps[0];
    const endAbs = stamps[stamps.length - 1];
    const nowAbs = eventDayDelta === -1 ? venueClock.minutes + 1440 : venueClock.minutes;
    if (nowAbs < startAbs || nowAbs > endAbs) return null;
    let index = -1;
    timelineAbs.forEach((m, i) => { if (m !== null && m <= nowAbs) index = i; });
    if (index < 0) return null;
    const from = timelineAbs[index];
    let nextIdx = -1;
    for (let j = index + 1; j < timelineAbs.length; j += 1) {
      if (timelineAbs[j] !== null) { nextIdx = j; break; }
    }
    const to = nextIdx >= 0 ? timelineAbs[nextIdx] : null;
    const progress = to != null && to > from ? Math.min(1, Math.max(0, (nowAbs - from) / (to - from))) : 0;
    return { index, progress };
  })();

  /* Countdown to your slot — gig day only (the day itself and the small hours after it, the
     same window the live timeline uses). Measured on the timeline's continuous scale, so a set
     or curfew past midnight counts correctly. Phases: before your set, on stage, after your
     set until the curfew, then done. */
  const countdown = (() => {
    if (!clockIsRelevant) return null;
    const at = (pred) => { const i = timelineEvents.findIndex(pred); return i >= 0 ? timelineAbs[i] : null; };
    const showStart = at((e) => e.label === 'Show Start');
    if (showStart == null) return null;
    const showEnd = at((e) => e.label === 'Show End');
    const curfewAt = at((e) => e.label.startsWith('Curfew'));
    const nowAbs = eventDayDelta === -1 ? venueClock.minutes + 1440 : venueClock.minutes;
    if (nowAbs < showStart) return { phase: 'before', mins: showStart - nowAbs };
    if (showEnd != null && nowAbs < showEnd) return { phase: 'on', mins: showEnd - nowAbs };
    if (curfewAt != null && nowAbs < curfewAt) return { phase: 'curfew', mins: curfewAt - nowAbs };
    return { phase: 'done' };
  })();
  /* Is the gig over? Expired, or its night has passed: two or more days back, the small hours
     after it with no live timeline, or the countdown has run out (past the curfew, or past the
     show end when there is none). Contacts' hours stop meaning "call now" from then on. */
  const gigOver = isExpired
    || (eventDayDelta != null && eventDayDelta < -1)
    || (eventDayDelta === -1 && !countdown)
    || countdown?.phase === 'done';
  // The line under the live timeline row: your slot in --role-yours, the curfew in --role-limit.
  const countdownLine = !countdown ? null
    : countdown.phase === 'before' ? { text: `On in ${fmtCountdown(countdown.mins)}`, color: 'var(--role-yours)' }
    : countdown.phase === 'on' ? { text: `On stage · ${fmtCountdown(countdown.mins)} left`, color: 'var(--role-yours)' }
    : countdown.phase === 'curfew' ? { text: `Curfew in ${fmtCountdown(countdown.mins)}`, color: 'var(--role-limit)' }
    : null;
  // "In 6 days", "Today": counted from the venue's today, not the device's.
  const relativeDay = request.eventDate ? formatRelativeTime(request.eventDate, { today: venueClock.date }) : null;

  /* How long each row's activity lasts — only rows with their own end time (the checks and the
     set). It used to be the gap until the next row, which read as the activity's length:
     "Show End · 2h 10m" looked like a two-hour show. The gaps live in the On Site / Downtime
     strip instead. */
  /* The set's length is said once. When the presence strip is up, its On Stage figure already
     is that length, so the Show Start row does not repeat it; the lit rail still spans the set. */
  const timelineIntervals = timelineEvents.map((ev) => ({
    mins: ev.span > 0 && !(ev.label === 'Show Start' && totalPresence != null && ev.span === showTime) ? ev.span : null,
  }));

  // ZONE 4a presence summary — three equal stats under the Day Timeline header
  // Performance shape (sets / length / break / mode). It reads as a footnote to the
  // presence strip's On Stage figure, so it lives in the Day Timeline card; without a
  // timeline it falls back to the header row where it used to sit.
  /* What you actually play, as SummaryLine parts. Set count and length are one fact, not two,
     so they share a part: `2 × 75m sets`. */
  /* Staying on-site is a term of the break between two sets at one venue, so it only exists
     when there are two or more sets. It closes the footing line where there is one, and falls
     back to a pill under the day where there is not (tech crew, no set data). */
  const multiSet = request.roleExecution?.setsCount >= 2;
  const stayNote = multiSet && tl.canLeaveDuringDowntime === false ? { text: 'Must stay on-site', tone: 'neutral' }
    : multiSet && tl.canLeaveDuringDowntime === true ? { text: 'Can leave', tone: 'ok' }
    : null;
  const perfSummary = (() => {
    const rx = request.roleExecution;
    // Sets, break and line-up describe the act. On a tech crew request (FOH, lights, stage)
    // they are the band's numbers, not the job, so the summary is a musician-only footing.
    if (!rx || request.roleTypeNeeded !== 'MUSICIAN') return [];
    const multi = multiSet;
    const hasBreak = multi && rx.breakLengthMinutes != null;
    return [
      /* One set whose length the day already shows (On Stage, or the Show Start row) adds
         nothing; a second set is new information, so Sets stays. */
      rx.setLengthMinutes != null && (multi || showTime == null)
        ? { num: multi ? `${rx.setsCount} × ${rx.setLengthMinutes}m` : `${rx.setLengthMinutes}m`, time: true, word: multi ? 'sets' : 'set' }
        : null,
      hasBreak ? { num: `${rx.breakLengthMinutes}m`, time: true, word: rx.setsCount > 2 ? 'breaks' : 'break' } : null,
    ].filter(Boolean);
  })();
  /* Staying on-site is said on its own line under the sets and breaks. As a third part of the
     same line it did not fit a 393px phone ("3 × 45m sets · 15m breaks · must stay on-site"
     wrapped and left a dot hanging), so it always takes the second line, at every width. */
  const stayInFooting = perfSummary.length > 0 && !!stayNote;
  /* The gig's facts (rendered in the header, under the place): how big the room is, where you
     sit on the bill, who you play with, and where. The ticket keeps only its terms (date, time,
     fee). Bill and line-up describe the act, so they are
     musician requests only; on a crew request they would describe the band, not the job. An
     audience the venue did not give is said, not skipped: it is the fact that sets the stakes.
     A stage open to the weather is a real constraint and is said in the limit colour. */
  const isMusician = request.roleTypeNeeded === 'MUSICIAN';
  const settingIcon = SETTING_VALUE[env?.setting]?.[0];
  const stageWords = SETTING_FACT[env?.setting] && env.coveredStage != null && ['outdoor', 'mixed'].includes(env.setting)
    ? (env.coveredStage ? ', covered stage' : <span style={{ color: 'var(--role-limit)' }}>, open stage</span>)
    : null;
  const gigFacts = [
    { key: 'crowd', testid: 'expected-attendance', icon: Users, muted: !request.expectedAttendance,
      text: request.expectedAttendance ? `Audience of ${formatCount(request.expectedAttendance)}` : 'Audience not given' },
    isMusician && BILL_FACT[request.roleExecution?.billPosition] && { key: 'bill', testid: 'bill-position', icon: ListOrdered, text: BILL_FACT[request.roleExecution.billPosition] },
    isMusician && LINEUP_FACT[request.roleExecution?.performanceMode] && { key: 'lineup', testid: 'line-up', icon: Guitar, text: LINEUP_FACT[request.roleExecution.performanceMode] },
    SETTING_FACT[env?.setting] && { key: 'setting', testid: 'setting', icon: settingIcon, text: <>{SETTING_FACT[env.setting]}{stageWords}</> },
  ].filter(Boolean);

  /* The venue's own words with its Read More, shared by both layouts (one is mounted at a
     time, so descRef always points at the one on screen). */
  const descriptionNote = request.description ? (<>
        <p ref={descRef} className={cn('rd-venue-note-text', !descExpanded && 'line-clamp-3')} data-testid="request-description">
          {request.description}
        </p>
        {/* The hit area is padded out to 44px and pulled back with a matching negative
            margin, so the control is thumb-sized without opening a gap under the note. */}
        {(descExpanded || descClamped) && (
          <button
            type="button"
            onClick={() => setDescExpanded(!descExpanded)}
            aria-expanded={descExpanded}
            className="rd-readmore text-sm md:text-xs inline-flex items-center underline underline-offset-2 transition-colors text-[color:var(--t1)] hover:text-[color:var(--t0)] active:text-[color:var(--t0)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--role-money)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--s8-bg-base)]"
            style={{ minHeight: '44px', paddingBlock: '12px', marginBlock: '-8px', borderRadius: 'var(--s8-radius-xs)' }}
          >
            {descExpanded ? 'Show Less' : 'Read More'}
          </button>
        )}
  </>) : null;

  /* What the gig is, as one block between the title and the ticket: the venue's
        facts (how big the room is, where you sit on the bill, who you play with, and
        where), then the venue's own words. Both come from the venue, so they share its label. The facts
        are quiet, the page's caption voice with --t3 icons, so the ticket stays the
        loudest thing here; one fact per item, and a phone wraps between facts, never
        within one. Below 1024px it opens the first tile, above the ticket; in the desktop pane
     it sits in the header beside the decision, so the ticket and the column beside it start
     level. */
  const venueBlock = (gigFacts.length > 0 || request.description) ? (
      <div className="rd-venue-note" data-testid="venue-note">
        <EyebrowLabel className="block">From {venueName || 'the Venue'}</EyebrowLabel>
        <ul className="rd-facts" data-testid="gig-facts">
          {gigFacts.map(({ key, testid, icon: Icon, text, muted }) => (
            <li key={key} className="rd-fact" data-testid={testid} style={muted ? { color: 'var(--t3)' } : undefined}>
              {Icon && <Icon aria-hidden className="rd-fact-icon" />}
              <span>{text}</span>
            </li>
          ))}
        </ul>
        {descriptionNote}
      </div>
    ) : null;


  /* ── Phone and tablet (<1024px): the top of the page, in reading order. Under the title, a
     status line (open, and until when); then the place as its own card (the venue's photo
     when it has one, its name and city, and the setting as the card's foot); then the terms
     as one strip of three (fee, date, set); then Add to Calendar and Share as their own row;
     then the gig itself (audience, bill, line-up) as chips over the venue's words. The desktop
     pane keeps the ticket. ── */
  const statusPill = (urg.label === 'URGENT' || urg.label === 'SOON') ? (
    <span className={cn('inline-flex items-center px-2 text-[11px] leading-[20px] font-semibold uppercase tracking-widest border', urg.cls)} style={{ borderRadius: 'var(--s8-radius-xs)' }} data-testid="urgency-badge">
      {urg.label}
    </span>
  ) : null;
  /* The closing day is the venue's date of expiresAt, the moment the backend flips the request
     to EXPIRED, so "closes Fri 9 Oct" and the flip can never disagree. */
  const expiresAt = request.expiresAt ? new Date(request.expiresAt) : null;
  const closesOn = expiresAt && !Number.isNaN(expiresAt.getTime()) ? formatDate(venueNow(expiresAt).date) : null;
  const statusLine = request.status === 'OPEN' ? (
    <div className="rd-status" data-testid="request-status">
      <span className="rd-status-dot" aria-hidden />
      <span>Open{closesOn && <> · closes {closesOn}</>}</span>
      {statusPill}
    </div>
  ) : null;

  /* The venue's photo, set by the venue on its profile page. A venue without one gets its name
     and city on the card face. */
  const venuePhoto = apiAssetUrl(request.venue?.photoUrl);
  /* The setting is the card's foot only when it says something about the weather: outdoors,
     indoors and out, a covered or open stage. Plain indoors is the usual case, so it is a chip
     under The Gig instead of a full row. */
  const settingFact = env?.setting !== 'indoor' ? gigFacts.find((f) => f.key === 'setting') : null;
  const placeHead = (<>
    {venuePhoto && <img className="rd-place-photo" src={venuePhoto} alt="" />}
    <span className="rd-place-row">
      <span className="rd-place-text">
        <span className="rd-place-name">{venueName}</span>
        {request.city && <span className="rd-place-city">{request.city}</span>}
      </span>
      {request.venue && <ChevronRight aria-hidden className="rd-place-chevron" />}
    </span>
  </>);
  const placeCard = venueName ? (
    <div className={cn('rd-place', venuePhoto && 'rd-place--photo')} data-testid="venue-card">
      {request.venue
        ? <Link to={`/venues/${request.venue.id}`} className="rd-place-head" data-testid="detail-venue-line">{placeHead}</Link>
        : <div className="rd-place-head" data-testid="detail-venue-line">{placeHead}</div>}
      {settingFact && (
        <div className="rd-place-foot" data-testid={settingFact.testid}>
          {settingFact.icon && <settingFact.icon aria-hidden className="rd-place-foot-icon" />}
          <span>{settingFact.text}</span>
        </div>
      )}
    </div>
  ) : null;

  /* The terms, three columns of label, value and what qualifies it. The fee keeps the page's
     one amber; a longer amount steps down so it holds its column. */
  const startT = bt.startTime || request.startTime;
  const feeText = request.budgetAmount ? `€${formatCount(request.budgetAmount)}` : null;
  const notGiven = <span className="rd-term-missing">Not given</span>;
  const termsCard = (
    <dl className="rd-terms" data-testid="decision-grid">
      <div className="rd-term">
        <dt className="rd-term-label">Fee</dt>
        <dd className="rd-term-value" data-testid="budget-display">
          {feeText ? <span className="rd-term-fee" data-len={feeText.length > 6 ? 'l' : feeText.length > 5 ? 'm' : undefined}>{feeText}</span> : notGiven}
        </dd>
        <dd className="rd-term-sub">
          {request.budgetAmount && (request.rateType || bt.payRateType) ? (request.rateType ? toSentence(getRateTypeLabel(request.rateType)).toLowerCase() : bt.payRateType) : null}
        </dd>
      </div>
      <div className="rd-term">
        <dt className="rd-term-label">Date</dt>
        <dd className="rd-term-value" data-testid="ticket-date">{request.eventDate ? formatDate(request.eventDate) : notGiven}</dd>
        <dd className="rd-term-sub" data-testid="relative-time-hint" style={countdown ? { color: 'var(--role-yours)' } : undefined}>
          {request.eventDate && countdown
            ? (countdown.phase === 'before' ? `On in ${fmtCountdown(countdown.mins)}` : countdown.phase === 'on' ? 'On now' : 'Done')
            : relativeDay ? sentenceCase(relativeDay) : null}
        </dd>
      </div>
      {/* The call time, not the set: when to be there is what a musician or crew member plans
          the day around. The set start is the line under it. With no call time the venue has
          not said one, so the set start is not passed off as it. */}
      <div className="rd-term">
        <dt className="rd-term-label">Call Time</dt>
        <dd className="rd-term-value" data-testid="terms-call-time">{tl.callTime ? <span className="rd-term-time">{formatTime(tl.callTime)}</span> : notGiven}</dd>
        <dd className="rd-term-sub">
          {startT ? <>Set {formatTime(startT)}</> : null}
        </dd>
      </div>
    </dl>
  );

  /* The gig, as chips: audience, bill, line-up, and plain indoors. Any other setting is the
     place card's foot, so it is not said twice. The audience leads, brighter, its number in
     semibold (.rd-chip--lead). */
  const chipFacts = gigFacts.filter((f) => f.key !== 'setting' || !settingFact);
  const gigBlock = (chipFacts.length > 0 || request.description) ? (
    <section className="rd-gig" data-testid="venue-note">
      <h2 className="rd-gig-title">The Gig</h2>
      {chipFacts.length > 0 && (
        <ul className="rd-chips" data-testid="gig-facts">
          {chipFacts.map(({ key, testid, icon: Icon, text, muted }) => {
            const lead = key === 'crowd' && !muted;
            return (
              <li key={key} className={cn('rd-chip', lead && 'rd-chip--lead')} data-testid={testid} style={muted ? { color: 'var(--t3)' } : undefined}>
                {Icon && <Icon aria-hidden className="rd-chip-icon" />}
                <span>{lead ? <>Audience of <strong>{formatCount(request.expectedAttendance)}</strong></> : text}</span>
              </li>
            );
          })}
        </ul>
      )}
      {descriptionNote}
    </section>
  ) : null;


  const presenceParts = totalPresence != null ? [
    { val: fmtDur(totalPresence), label: 'On Site' },
    { val: showTime != null ? fmtDur(showTime) : <NotGiven />, label: 'On Stage' },
    // Paid is a term of the downtime, so it is said on the figure rather than as a pill below the day.
    { val: downtime != null ? fmtDur(downtime) : <NotGiven />, label: 'Downtime', note: tl.downtimePaid ? 'Paid' : null },
  ] : [];

  /* What you actually play, centred above the three hours of the day: the line introduces
     the strip rather than trailing off it, and stays one line of text, never a second grid. */
  const presenceSummary = (presenceParts.length > 0 || perfSummary.length > 0) ? (
    <div className="mb-4" data-testid="presence-summary">
      {presenceParts.length > 0 && (
        <div
          className="grid grid-cols-3 px-1 py-2"
          style={{ fontFamily: 'var(--s8-font-mono)' }}
        >
          {presenceParts.map((p, i) => (
            <div
              key={p.label}
              className="min-w-0 px-2 py-1 text-center"
              style={i < presenceParts.length - 1 ? { borderRight: '1px solid var(--s8-b1-solid)' } : undefined}
            >
              {/* The page's label voice (condensed caps), not the mono the value is set in — so a
                  label never reads as another number. */}
              <EyebrowLabel className="block" style={{ lineHeight: 1.2 }}>{p.label}</EyebrowLabel>
              <div className="whitespace-normal break-words" style={{ marginTop: '2px', fontSize: 'var(--s8-fs-lead)', lineHeight: 1.1, color: 'var(--t0)', fontWeight: 500, wordSpacing: 'var(--rd-mono-word-gap)' }}>
                {p.val}
              </div>
              {p.note && (
                <div className="mt-1" style={{ fontFamily: 'var(--s8-font-body)', fontSize: 'var(--s8-fs-caption)', lineHeight: 1.3, color: 'var(--role-covered)' }}>
                  {p.note}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  // ZONE 4 day-timeline card
  // Timeline geometry. The time column is mono and has no slack — five characters at DM Mono's
  // 0.6em advance is exactly 3em of ink, so the column width IS the font size times three and
  // the rail and label offsets hang off it. Measured: at the old 16px the ink was 48.00px in a
  // 48px box. Raising the type without these moving together clips the hour.
  const TL_FS_TIME = 18;                      // px — mono, the left time column
  const TL_COL_TIME = TL_FS_TIME * 3;         // 54px of ink, no padding to spare
  const TL_RAIL = TL_COL_TIME + 14;           // dot centre: 14px of air after the time
  const TL_LABEL = TL_RAIL + 18;              // label starts clear of the 11px dot + its ring
  const TL_GAP = 20;                          // px between dots — was 15; the rows needed air
  /* The row's line box, fixed in px. Everything vertical in a row is measured off it, which
     is what makes the rhythm a rhythm: time, dot and label share its centre line, so row
     heights, dot spacing and the card's height hold still as the clock moves. */
  const TL_LINE = 24;                         // px — one line
  const TL_MID = TL_LINE / 2;                 // the centre line: time, dot and label share it
  const TL_DOT = 11;                          // px — the dot's diameter, border included
  const timelineCard = timelineEvents.length > 0 ? (
        <Section tier="anchor">
          <SectionHeader icon={Timeline}>Day Timeline</SectionHeader>
          {presenceSummary}

          {/* Vertical timeline — compact rail; the activity's duration fills the row's right side */}
          <div className="relative">
            {timelineEvents.map((ev, i) => {
              const iv = timelineIntervals[i];
              const isLast = i === timelineEvents.length - 1;
              const isRed = ev.priority === 'red';
              const isMuted = ev.priority === 'muted';
              const isWarning = ev.variant === 'warning';
              const isStatus = ev.variant === 'status';
              /* A dot's fill now means the day has reached it, not that it matters more than its
                 neighbours. Everything ahead of the clock is hollow and fills as time sweeps
                 past it; the outline keeps the row's weight, so Show Start is still the brightest
                 ring and Curfew still red before either has happened. On any day the clock
                 cannot speak to, nothing has been reached, so every dot is hollow. */
              const isShowStart = ev.label === 'Show Start';
              // The musician's own moments take the brightest ink (--role-yours); every other row
              // steps down to secondary text.
              const isYours = isShowStart || ev.label.startsWith('Your ');
              // startsWith: a fixed hard stop is said on this row as "Curfew (hard stop)".
              const isCurfew = ev.label.startsWith('Curfew');
              const dotAccent = isShowStart ? 'var(--role-yours)' : isCurfew ? 'var(--role-limit)' : 'var(--s8-text-dim)';
              // The interval beginning at this row spans the on-stage block when it
              // runs Show Start → Show End. Emphasise it via brightness + weight, not
              // a new hue (amber stays reserved for money + the primary action).
              const nextEv = timelineEvents[i + 1];
              const isShowSpan = isShowStart && nextEv && nextEv.label === 'Show End';
              /* Live scale. The row holding the clock is lifted off the card; the rail above it
                 is drawn as traversed, and its own segment fills to wherever the clock sits —
                 that fill is what slides, a little further every half minute. Null on any day
                 this timeline does not belong to, and every row renders exactly as before. */
              const isNow = livePosition?.index === i;
              const isPast = livePosition != null && i < livePosition.index;
              // One grammar for every stretch (see the --rd-rail-* tokens): hue by whose it is,
              // brightness by whether the day has passed it. Your set and the curfew run trace too.
              const railTone = isShowSpan ? '-set' : isCurfew ? '-limit' : '';
              const railPast = `var(--rd-rail${railTone}-past)`;
              const railAhead = `var(--rd-rail${railTone}-ahead)`;
              const pct = Math.round((livePosition?.progress ?? 0) * 100);
              const railFill = isNow
                ? `linear-gradient(to bottom, ${railPast} 0%, ${railPast} ${pct}%, ${railAhead} ${pct}%)`
                : (isPast ? railPast : railAhead);
              return (
                <div
                  key={ev.time ? `${ev.time}-${ev.label}` : `tl-${i}`}
                  className={cn('relative rd-tl-row', isNow && 'rd-tl-row--now')}
                  aria-current={isNow ? 'true' : undefined}
                  style={{ marginBottom: isLast ? 0 : `${TL_GAP}px` }}
                >
                  {/* Connector — runs from this dot down to the next.
                      Straight, always. This started as a 2px div per row, and because every
                      row leaned by a different amount the segments stood at different offsets:
                      the rail met each circle on one x and left it on another, up to 3.7px of
                      step, which read as the dots being struck through. Curving the joins made
                      it continuous but not right — a rail that bows is a crooked spine, and
                      this rail is the day's axis. An axis that bends reads as a mistake rather
                      than as motion.
                      So the lean is gone from the row entirely, and with it the ragged left
                      edge it gave the labels. "Now" is said by the plate that lifts the current
                      row off the card, the rail filling to the clock and the dots filling as
                      they are passed; the type holds one size, so nothing on the card moves. */}
                  {!isLast && (
                    /* Every stretch carries the live fill in its own hue: your set (Show Start →
                       Show End) in ivory, the run from Curfew to Load-out in coral and dashed (a
                       mask over the same fill, so the dashes trace too), the rest grey. Every rail
                       colour is its dots' ink at a fraction (see the --rd-rail-* tokens), so the
                       dots are always the loudest marks. Every
                       segment is 2px and meets the dots: the others run dot centre to dot centre
                       under the (opaque) dots, the set's from dot edge to dot edge. The dots used to
                       wear a 4px card ring that masked the rail's ends, which left a 4px gap at
                       every grey dot and made the rail read as broken, while the set touched. */
                    <div aria-hidden className="absolute" style={{
                      left: `${TL_RAIL}px`, width: '2px',
                      ...(isShowSpan
                        ? { top: `${TL_MID + TL_DOT / 2}px`, height: `calc(100% + ${TL_GAP - TL_DOT}px)`, zIndex: 1 }
                        : { top: `${TL_MID}px`, height: `calc(100% + ${TL_GAP}px)` }),
                      transform: 'translateX(-50%)',
                      background: railFill,
                      ...(isCurfew ? {
                        WebkitMaskImage: 'repeating-linear-gradient(to bottom, #000 0 4px, transparent 4px 8px)',
                        maskImage: 'repeating-linear-gradient(to bottom, #000 0 4px, transparent 4px 8px)',
                      } : null),
                    }} />
                  )}
                  {/* Time (left column). A row with no time of its own leaves the column empty. */}
                  <span className="absolute text-right" style={{
                    left: 0, top: 0, width: `${TL_COL_TIME}px`,
                    fontFamily: 'var(--s8-font-mono)', fontSize: `${TL_FS_TIME}px`, fontWeight: 500,
                    fontVariantNumeric: 'tabular-nums', lineHeight: `${TL_LINE}px`,
                    color: isWarning ? 'var(--s8-text-muted)' : isYours ? 'var(--role-yours)' : isRed ? 'var(--role-limit)' : isMuted ? 'var(--t3)' : isStatus ? 'var(--t3)' : 'var(--t1)',
                  }}>
                    {isStatus || isWarning ? null : ev.time}
                  </span>
                  {/* Dot on the rail: opaque, so the rail meets its edge rather than running through */}
                  <div className="absolute rounded-full" style={{
                    left: `${TL_RAIL}px`, top: `${TL_MID}px`, width: `${TL_DOT}px`, height: `${TL_DOT}px`,
                    transform: 'translate(-50%, -50%)',
                    transition: 'background-color 600ms ease-out, border-color 600ms ease-out',
                    ...(isPast || isNow
                      ? { background: dotAccent, border: `1px solid ${dotAccent}` }
                      : { background: isNow ? 'var(--rd-raised)' : 'var(--s8-bg-card)', border: `1.5px solid ${dotAccent}` }),
                  }} />
                  {/* Label · dotted leader (fills the blank canvas) · the activity's own duration.
                      Label keeps its natural width so it's never squeezed; the leader
                      stretches to fill whatever room remains, tying the event to its
                      duration — legible at any card width without adding height. */}
                  {/* position: relative puts this in the positioned paint layer alongside the
                      time, the dot and the lifted row's plate. In normal flow it painted in an
                      earlier phase than any of them, so the plate covered the label whole. */}
                  {/* flex-start, not baseline: the label and the duration now share one fixed
                      line box, so their first lines coincide exactly — and unlike baseline it
                      still holds the duration to the FIRST line when a label wraps to two. */}
                  <div style={{ position: 'relative', marginLeft: `${TL_LABEL}px`, display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {/* The label is whole on its line; whose check it is ("headliner") sits under
                        it as a muted sub-line at the secondary size. Nothing in the timeline
                        truncates. */}
                    {/* Never narrower than its longest word, so a label wraps rather than
                        spilling. When the row runs short (320px: a 152px row), the leader
                        gives way to nothing and the duration's box to its own text first. */}
                    <span style={{
                      flex: '0 1 auto', minWidth: 'min-content',
                      fontFamily: 'var(--s8-font-body)', fontSize: 'var(--s8-fs-lead)', lineHeight: `${TL_LINE}px`,
                      // Roles change colour only, never weight: "yours" rows share the others' weight.
                      fontWeight: isMuted ? 400 : 500,
                      color: isWarning ? 'var(--s8-text-muted)' : isRed ? 'var(--role-limit)' : isYours ? 'var(--role-yours)' : isMuted ? 'var(--t3)' : 'var(--t1)',
                    }}>
                      {ev.label}
                      {isStatus && ev.status && <span style={{ color: 'var(--t3)' }}> · {ev.status}</span>}
                      {ev.suffix && (
                        <span style={{ display: 'block', marginTop: '2px', fontWeight: 400, fontSize: 'var(--s8-fs-caption)', lineHeight: 1.3, color: 'var(--t3)' }}>{ev.suffix}</span>
                      )}
                      {isNow && countdownLine && (
                        <span style={{ display: 'block', marginTop: '2px', fontWeight: 400, fontSize: 'var(--s8-fs-caption)', lineHeight: 1.3, color: countdownLine.color }} data-testid="timeline-countdown">{countdownLine.text}</span>
                      )}
                      {isWarning && ev.subtext && (
                        <span style={{ display: 'block', marginTop: '3px', fontWeight: 400, fontSize: 'var(--s8-fs-body)', color: 'var(--t3)', lineHeight: 1.4 }}>{ev.subtext}</span>
                      )}
                    </span>
                    {iv.mins != null && (
                      <>
                        {/* Dotted leader — absorbs the blank width between label and value */}
                        <span aria-hidden style={{ flex: '1 1 0', minWidth: 0, alignSelf: 'flex-start', marginTop: `${TL_MID}px`, height: 0, borderTop: `1.5px dotted ${isShowSpan ? 'var(--s8-text-dim)' : 'var(--s8-b2-solid)'}` }} />
                        {/* Duration of the activity itself */}
                        <span style={{
                          // Shrinks well before the label does (weight 100 to its 1), down to its own text.
                          flex: '0 100 58px', minWidth: 'max-content', textAlign: 'right', whiteSpace: 'nowrap', lineHeight: `${TL_LINE}px`,
                          fontFamily: 'var(--s8-font-mono)', fontSize: 'var(--s8-fs-body)', fontVariantNumeric: 'tabular-nums', wordSpacing: 'var(--rd-mono-word-gap)',
                          fontWeight: isShowSpan ? 600 : 400,
                          color: isShowSpan ? 'var(--t1)' : 'var(--t3)',
                        }}>
                          {fmtDur(iv.mins)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Condition pills. Downtime pay is said on the Downtime figure and staying on-site in
              the footing line; a pill is left only for what has no figure to sit on. The role
              colour is on the text only; the pill itself is a neutral hairline, never a tint. */}
          {((tl.downtimePaid && totalPresence == null) || (stayNote && !stayInFooting) || (bt.hardStop && bt.hardStop !== 'Flexible' && !curfewIsHardStop)) && (
          <div className="flex flex-wrap gap-2.5 mt-5">
            {tl.downtimePaid && totalPresence == null && (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm" style={{ color: 'var(--role-covered)', border: '1px solid var(--s8-b2-solid)', borderRadius: 'var(--s8-radius-sm)' }}>
                <CheckCircle2 className="h-4 w-4" /> Downtime Paid
              </span>
            )}
            {stayNote && !stayInFooting && (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm" style={stayNote.tone === 'ok'
                ? { color: 'var(--role-covered)', border: '1px solid var(--s8-b2-solid)', borderRadius: 'var(--s8-radius-sm)' }
                : { color: 'var(--t1)', border: '1px solid var(--s8-b2-solid)', borderRadius: 'var(--s8-radius-sm)' }}>
                {stayNote.tone === 'ok' && <CheckCircle2 className="h-4 w-4" />}
                {stayNote.tone === 'ok' ? 'Can leave during breaks' : stayNote.text}
              </span>
            )}
            {bt.hardStop && bt.hardStop !== 'Flexible' && !curfewIsHardStop && (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm" style={{ color: 'var(--role-limit)', border: '1px solid var(--s8-b2-solid)', borderRadius: 'var(--s8-radius-sm)' }}>
                Hard stop: {bt.hardStop}
              </span>
            )}
          </div>
          )}


      {/* What you play, as one summary line under the day, in the voice Setlist opens with:
          "2 × 75m sets · 30m break · free to leave". It was a second centred strip of
          label-over-value cells that mirrored the presence strip at the top of the card. */}
      {perfSummary.length > 0 && (
        <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--s8-b1-solid)' }} data-testid="performance-summary">
          <SummaryLine parts={perfSummary} />
          {stayInFooting && (
            <div className="flex items-center gap-1.5 mt-1" style={{ fontFamily: 'var(--s8-font-body)', fontSize: 'var(--s8-fs-caption)', lineHeight: 1.5, color: stayNote.tone === 'ok' ? 'var(--role-covered)' : 'var(--t2)' }} data-testid="stay-note">
              {stayNote.tone === 'ok' && <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />}
              {stayNote.tone === 'ok' ? `Free to leave between sets` : 'Must stay on-site between sets'}
            </div>
          )}
        </div>
      )}
          {/* Operational note */}
          {tl.notes && (
            <div className="mt-4 pl-3 text-sm italic leading-relaxed" style={{ color: 'var(--t2)', borderLeft: '2px solid var(--s8-b2-solid)' }}>
              {tl.notes}
            </div>
          )}

        </Section>
  ) : null;

  // ZONE 6 equipment card
  const equipmentCard = (venueGear.length > 0 || artistGear.length > 0) ? (
        <Section tier="standard">
          <SectionHeader icon={MicVocal}>Equipment</SectionHeader>

          {/* Ownership split as two scannable lists. Side-by-side when the card is
              wide (uses the horizontal room), stacked when it's narrow — driven by
              the card's own width. Green ticks = covered by the venue; the You bring rows are
              the musician's own packing checklist. */}
          <div className="equip-split-wrap">
            <div className="equip-split">
              {/* You bring leads: it is the one list to act on (pack it, tick it). What the venue
                  provides is reference, what not to pack, so it follows, set closer. */}
              {artistGear.length > 0 && <PackingList gear={artistGear} gigId={request.id} eventDayDelta={eventDayDelta} />}

              {venueGear.length > 0 && (
                <div className="equip-col">
                  {/* Column head (see EQ_HEAD). No count: the five rows are right there, and nothing
                      is done with the number. */}
                  <h3 className="mb-3" style={EQ_HEAD}>Venue Provides</h3>
                  <ul>
                    {venueGear.map((g, i) => (
                      /* Reference, not a checklist: nothing here is tapped, so the rows close up to
                         a 32px pitch (the checklist above keeps its 44px tap rows). */
                      <li key={`vg-${g}-${i}`} className="flex items-start gap-2.5 py-1">
                        {/* Marker sits in a first-line-height box so it optically centres on the first line even when the item wraps */}
                        <span className="shrink-0 flex items-center justify-center" style={{ width: `${EQ_MARK + 1}px`, height: EQ_MARK_BOX }}>
                          <Check style={{ width: `${EQ_MARK}px`, height: `${EQ_MARK}px`, color: 'var(--role-covered)' }} />
                        </span>
                        <span style={{ fontFamily: 'var(--s8-font-body)', fontSize: EQ_FS, lineHeight: EQ_LH, fontWeight: 400, color: 'var(--t1)' }}>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </Section>
  ) : null;

  // ZONE 5b setlist card — collapsed to the first rows by default; only the minority class is badged
  /* Set to the Day Timeline and the Equipment list: the title carries the same 18px the event
     labels and the gear items do, and the mono metadata takes the timeline's 16px duration
     size. Rows sit on the card's own inset, with no fills and no dividers. */
  const SL_FS_TITLE = 'var(--s8-fs-lead)';   // 18px — body, the song title
  const SL_FS_META = 'var(--s8-fs-body)';    // 16px — mono, the order number and the duration (timeline's size)
  const SL_FS_SUB = 'var(--s8-fs-caption)';  // 15px — body, line 2: the composer and the per-song note
  const SL_LH = 1.35;       // shared leading, so the number, title and duration sit on one line
  const setlistCard = (request.setlist && request.setlist.length > 0) ? (
    <Section tier="standard" data-testid="setlist-section">
      <SectionHeader icon={ListMusic}>Setlist</SectionHeader>
      {setlistHeaderRow}
      {(() => {
        const renderSong = (song, i) => {
          const isCover = isCoverTrack(song);
          const showBadge = setlistBadged === (isCover ? 'cover' : 'original');
          // Line 2: the composer (a cover's artist) and the note, in one muted run under the title.
          const sub = [isCover ? song.artist : null, song.notes].filter(Boolean).join(' · ');
          return (
            <div
              key={song.id || `song-${song.order}-${i}`}
              /* One layout for every row, no dividers; odd rows (1, 3, 5…) carry a soft band,
                 and the index runs on through the expanded rows, so the banding never restarts.
                 The number column is the Equipment checkmark column and its gap as one box
                 (17px + 10px), so titles start on the gear items' line and two-digit numbers
                 (19.2px in DM Mono) sit inside it instead of running 2px into the gap. The row
                 runs to the card edges (-mx-5 px-5) so the hover outline frames the whole row. */
              className={cn('rd-setlist-row flex items-start py-2.5 -mx-5 px-5', i % 2 === 0 && 'rd-setlist-row--band')}
              data-testid={`setlist-item-${i}`}
            >
              <span className="shrink-0 pt-0.5 whitespace-nowrap" style={{ minWidth: `${EQ_MARK + 1 + 10}px`, fontFamily: 'var(--s8-font-body)', fontSize: SL_FS_META, lineHeight: SL_LH, fontVariantNumeric: 'tabular-nums', color: 'var(--t3)' }}>
                {song.order}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span style={{ fontSize: SL_FS_TITLE, lineHeight: SL_LH, fontWeight: 500, color: 'var(--t0)' }}>{song.title}</span>
                  {showBadge && (
                    <span className="px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest" style={{ color: 'var(--t1)', border: '1px solid var(--s8-b2-solid)', borderRadius: 'var(--s8-radius-xs)' }}>{isCover ? 'Cover' : 'Original'}</span>
                  )}
                </div>
                {sub && (
                  <div className="mt-0.5" style={{ fontFamily: 'var(--s8-font-body)', fontSize: SL_FS_SUB, lineHeight: 1.4, color: 'var(--t2)' }}>{sub}</div>
                )}
              </div>
              {song.duration && (
                <span className="shrink-0 pt-0.5 ml-2.5" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: SL_FS_META, lineHeight: SL_LH, fontVariantNumeric: 'tabular-nums', color: 'var(--t2)' }}>{song.duration}</span>
              )}
            </div>
          );
        };
        const extraSongs = sortedSetlist.slice(SETLIST_PREVIEW_COUNT);
        return (
          <>
            <div>
              {sortedSetlist.slice(0, SETLIST_PREVIEW_COUNT).map(renderSong)}
            </div>
            {/* Remaining tracks stay mounted; .rd-collapse animates their height (grid 0fr → 1fr)
                so the list drops open from the preview rows. Inert while closed: no tab stops. */}
            {extraSongs.length > 0 && (
              /* The collapse clips its overflow (it animates height); it spans the card edge to
                 edge and pads the list back in, so nothing at the row edges is cut. */
              <SetlistMore total={sortedSetlist.length}>
                {extraSongs.map((song, j) => renderSong(song, j + SETLIST_PREVIEW_COUNT))}
              </SetlistMore>
            )}
          </>
        );
      })()}
    </Section>
  ) : null;

  return (
    <div
      className={cn('request-detail-page', !embedded && 'rd-page--docked')}
      style={{ fontFamily: 'var(--s8-font-body)' }}
      data-testid="request-detail-page"
    >
      {/* ── Action bar. Back, the decision, and save — the three controls a musician reaches
          for — docked at the foot of the screen, floating over the page the whole way down.
          Share is not here: it sits with Add to calendar under the decision grid.

          It sits OUTSIDE the drill box on purpose. Inside it, the box's transform made itself
          the containing block for anything fixed, so the dock had to be held back until the
          slide finished — measured, that left the CTA in the DOM at 390ms and invisible until
          992ms. 600ms of a button you can see coming but cannot press. Out here there is no
          transform to wait out, so it arrives with the screen.

          It is also chrome rather than content, so not sliding in with the page is right. ── */}
      {!embedded && actionBar}
      {/* The drill animation runs on this inner box, not on the page root, because the root
          is what clips it. Travelling in from the right puts 26px of content past the right
          edge, and measured on a 390px phone that pushed scrollWidth to 416 for the first
          200ms — a horizontal scrollbar on arrival. The root's overflow-x: clip eats it.
          clip, not hidden: hidden would make the root a scroll container and the sticky
          action bar would start sticking to it instead of to the viewport.
          Arriving and leaving are the same gesture in reverse. Never both at once — two
          animation declarations on one element would cascade, not compose. */}
    <div className={cn('rd-drill', slidingOut ? 'drill-slide-out' : 'drill-slide-in')}>
      {/* ── ZONE 1: Header ─────────────────────────────────── */}
      {/* Fallback only: with no Day Timeline card there is nowhere else for the shape of the
          performance to live. It sits under the bar rather than in it — a line of text pinned
          over the page would be a second thing to read, not a control to reach for. */}
      {perfSummary.length > 0 && !timelineCard && (
        <div className="min-w-0 pb-3 px-2.5 md:px-0 text-base md:text-sm leading-snug" style={{ color: 'var(--t2)' }} data-testid="performance-summary">
          {[...perfSummary.map(p => [p.num, p.word].filter(Boolean).join(' ')), stayInFooting && (stayNote.tone === 'ok' ? 'free to leave between sets' : 'must stay on-site between sets')].filter(Boolean).join(' · ')}
        </div>
      )}

      {/* Desktop 2-col: Main + Sidebar */}
      <div className="detail-layout">
      {/* Docked, the page root owns the only bottom padding (one dock height + safe area + 16px);
          embedded, there is no dock and the main column keeps its own. */}
      <div className={cn('detail-main px-2.5 md:px-0', embedded ? 'pb-10 md:pb-8 lg:pb-4 detail-main--embedded' : 'pb-0')}>

      {/* ── Header block (breadcrumb → meta line): left inset = card border to card body text,
          measured 21px (1px border + p-5) at 390/1440/1920, so it lines up with card interior text ── */}
      {/* Spacing rhythm: tight inside a group (title → venue 8px), generous between groups
          (header → identity 24px, venue → description 16px, description → deal 28px). */}
      <div className="pt-2 rd-header-inset" data-testid="detail-header-block">
      {/* Pills row — only rendered when there is a pill, so it never adds an empty band. The
          urgency pill (URGENT / SOON) is not here: it rides the date cell's "In 4 days" line. */}
      {isExpired && (
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {isExpired && <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest border text-[var(--role-limit)] bg-[var(--role-limit)]/10 border-[var(--role-limit)]/25" style={{ borderRadius: 'var(--s8-radius-xs)' }}>Expired</span>}
      </div>
      )}

      {/* ── ZONE 2: Identity ───────────────────────────────── */}
      {/* The dash is bound to the word before it (U+00A0), so a wrapped title never opens a
          line on it. The bill position is not here: it is in the facts, under the title. */}
      {/* Identity and place as one text column. In the desktop pane (>=1024px), when the
          title fits one line beside it (titleBeside, measured), the decision leaves its own
          row and stands top right, level with the title's first line, and this column stops
          48px short of it (see .rd-header-main--beside). */}
      <div ref={headerMainRef} className={cn('rd-header-main', embedded && canShowInterest() && !isExpired && titleBeside && 'rd-header-main--beside')}>
      <div className="rd-title-block">
        <h1 className="m-0 rd-page-title" data-testid="request-title">
          {(request.title || '').replace('[DEMO] ', '').replace(/ \u2014/g, '\u00A0\u2014')}
        </h1>
      </div>
      {!wideLayout && statusLine}

      {/* Name before place, in the desktop pane (>=1024px): the venue and city read as the
          dateline under the heading, tight to the title. Below 1024px the place is printed on
          the ticket instead, under the hours, as a ticket prints the venue. */}
      {venueName && wideLayout && (
        <div className="rd-eyebrow" data-testid="detail-venue-line">
          {/* The link's own text is 18px tall; .rd-venue-link carries a 44px target around it
              without moving the text (see index.css). */}
          {request.venue ? (
            <Link to={`/venues/${request.venue.id}`} className="rd-venue-link hover:text-[var(--role-money)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--role-money)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--s8-bg-base)]">{venueName}</Link>
          ) : venueName}
          <span style={{ color: 'var(--t3)' }}> · </span>
          {/* A city is one name: "'s-Hertogenbosch" never breaks at its hyphen. */}
          <span style={{ color: 'var(--t3)', whiteSpace: 'nowrap' }}>{request.city}</span>
        </div>
      )}
      {/* The venue's block and the decision, side by side in the desktop pane: the block on
          the left, the decision right-aligned, ending flush with the tile field below. Below
          1024px the block opens the first tile instead and this row is empty (the phone keeps
          its docked bar). */}
      <div className="rd-lede-row">
        {wideLayout && venueBlock}
        {embedded && canShowInterest() && !isExpired && (
          <div className="rd-header-action">{actionBar}</div>
        )}
      </div>
      </div>{/* end rd-header-main */}

      <div className="rd-top-rule" aria-hidden />
      </div>{/* end header block */}



      {/* Three groups, each one subject, in the order a musician prepares: the day (when to be
          where, who to ring), then the music (what you play on, what you play), then the venue
          (getting there, being there). A group never splits across columns, so the columns are
          subjects rather than an arbitrary slice of a queue; how many columns there are is left
          to the pane's width. Stacked on a phone, the same order reads straight down. */}
      <div className="detail-tiles-wrap mt-6">
        <div className="detail-tiles">
          <div className="detail-tiles-col">
          {/* ── ZONE 3: desktop (>=1024px), the ticket (date, time, fee) and the utility keys; below it,
              the place card, the terms strip, the keys and the gig (see placeCard and termsCard). ── */}
          <div className="rd-tile">
            {wideLayout ? (<>
            <div
              className="decision-grid-card overflow-hidden rd-card rd-card--anchor"
              data-testid="decision-grid"
            >
              {/* The ticket: a stub with the date, torn off along a perforation, and the terms
                  beside it (when, how soon, what it pays), the way a ticket prints date, time and
                  price. The list card shows the same date as a stub, so opening a gig reads as
                  that stub getting bigger. Sizes are the ticket's own (.rd-ticket in index.css). */}
              <div className="rd-ticket">
                {request.eventDate && (() => {
                  const [dow, day, mon] = formatDate(request.eventDate).split(' ');
                  return (
                    <div className="rd-ticket-stub" data-testid="ticket-date">
                      <span className="rd-ticket-dow">{dow}</span>
                      <span className="rd-ticket-day">{day}</span>
                      <span className="rd-ticket-mon">{mon}</span>
                    </div>
                  );
                })()}
                <div className="rd-ticket-main">
                  {/* The range holds one line: Mono numerals, the separator drawn (TimeRange). */}
                  <div className="rd-ticket-time">
                    {/* No times at all: the drawn dash alone would stand in for them, so it says so,
                        naming what is missing (a bare "Not given" on a ticket could be anything). */}
                    {(bt.startTime || request.startTime || bt.endTime || request.endTime)
                      ? <TimeRange from={formatTime(bt.startTime || request.startTime)} to={formatTime(bt.endTime || request.endTime)} />
                      : <span className="rd-ticket-missing">Times not given</span>}
                  </div>
                  {/* The fee, the ticket's price: the only amber on the card. Fee type from
                      request.rateType, the field the list card reads, so the two screens cannot
                      disagree; the older free-text bookingTerms.payRateType is only the fallback. */}
                  <div className="rd-ticket-fee">
                    {request.budgetAmount
                      ? <span className="rd-ticket-amount" data-testid="budget-display">€{formatCount(request.budgetAmount)}</span>
                      : <span className="rd-ticket-missing" data-testid="budget-display">Fee not given</span>}
                    {/* The rate only qualifies an amount; with no amount there is nothing to qualify. */}
                    {request.budgetAmount && (request.rateType || bt.payRateType) && <span className="rd-ticket-rate">{request.rateType ? toSentence(getRateTypeLabel(request.rateType)).toLowerCase() : bt.payRateType}</span>}
                  </div>
                  {/* Relative hint, under the fee: "In 2 days", or on gig day your slot ("On in
                      1 h 12 m", "On now", "Done"). The urgency pill (URGENT / SOON) closes the line.
                      Last on the face, so time, place and price read as one block and how soon
                      follows them. */}
                      {(() => {
                        const urgencyPill = (urg.label === 'URGENT' || urg.label === 'SOON') && (
                          <span className={cn('inline-flex items-center px-2 text-[11px] leading-[16px] font-semibold uppercase tracking-widest border', urg.cls)} style={{ borderRadius: 'var(--s8-radius-xs)' }} data-testid="urgency-badge">
                            {urg.label}
                          </span>
                        );
                        /* The pill says what the words already say, so it gives way first: the line is
                           one 18px row that wraps and clips, and where the pill does not fit beside the
                           words (a 320px phone, or "Tomorrow" with URGENT) it drops to the clipped second
                           row instead of running into the column rule. */
                        const hintLine = { height: '18px', overflow: 'hidden', alignContent: 'flex-start' };
                        return request.eventDate && countdown ? (
                          <div className="flex flex-wrap items-center gap-x-2" style={{ ...hintLine, fontFamily: 'var(--s8-font-body)', fontSize: 'var(--s8-fs-caption)', lineHeight: '18px', color: 'var(--role-yours)', whiteSpace: 'nowrap' }} data-testid="relative-time-hint">
                            {countdown.phase === 'before' ? `On in ${fmtCountdown(countdown.mins)}` : countdown.phase === 'on' ? 'On now' : 'Done'}
                            {urgencyPill}
                          </div>
                        ) : relativeDay ? (
                          <div className="flex flex-wrap items-center gap-x-2" style={{ ...hintLine, fontFamily: 'var(--s8-font-body)', fontSize: 'var(--s8-fs-caption)', lineHeight: '18px', color: 'var(--t2)', whiteSpace: 'nowrap' }} data-testid="relative-time-hint">
                            {sentenceCase(relativeDay)}
                            {urgencyPill}
                          </div>
                        ) : urgencyPill ? <div className="flex">{urgencyPill}</div> : null;
                      })()}
                </div>
              </div>
            </div>
            {/* ── Utility keys, desktop (>=1024px): their own row under the decision grid, one
                column gap below it. Below 1024px the same two keys are their own row under the
                terms instead; only one of the two is ever mounted, so the testids stay unique. They
                share the grid's tile rather than having their own, so the column balancing can
                never part them from the card they belong to (on a sparse gig it moved them to
                the head of the next column). ── */}
            {utilityKeys && (
              <div className="rd-utility-row rd-ukeys grid gap-3" data-testid="utility-actions">{utilityKeys}</div>
            )}
            </>) : (<>
              {placeCard}
              {termsCard}
              {utilityKeys && (
                <div className="rd-gig-keys rd-ukeys grid gap-3" data-testid="utility-actions">{utilityKeys}</div>
              )}
              {gigBlock}
            </>)}
            {/* The venue's own words, under the terms. With the facts line and the ticket above,
                what is left in a description is what no field holds (the style of music, the
                crowd, the dress), so it reads as the venue's note, marked as theirs, not as the
                page's opening pitch. Three lines, Read More only when the clamp cuts something. */}
          </div>
            {tile(timelineCard)}
          </div>
          {(equipmentCard || setlistCard) && (
            <div className="detail-tiles-col">
              {tile(equipmentCard)}
              {tile(setlistCard)}
            </div>
          )}
          <div className="detail-tiles-col">
            {/* The on-site contact is venue information — venue staff, not part of the day's
                running order — so it heads the venue group rather than trailing the timeline.
                It also evens the three subjects out, which is what lets three columns balance
                (imbalance 417px → 118px at 1600). */}
            {tile(renderContactsCard())}
            {tile(logisticsCard)}
            {hasVenueFacts(hosp, catering) && tile(<VenueFactsStrip hosp={hosp} catering={catering} />)}
          </div>
        </div>
      </div>

      {/* ── ZONE 10b: Interested Talent (venue only) ──────── */}
      {user?.profileType === 'VENUE' && interests.length > 0 && (
        <Section tier="standard" data-testid="interested-talent-section" style={{ marginTop: 'var(--rd-col-gap)' }}>
          <SectionHeader icon={Users}>Interested Talent</SectionHeader>
          <div className="text-xs mb-4" style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--t3)' }}>
            {interests.length} {interests.length === 1 ? 'response' : 'responses'}
          </div>
          <div className="space-y-3">
            {interests.map(t => (
              <div key={t.interestId} className="p-4 transition-colors" style={{ background: 'var(--bg-card-inner)', border: '1px solid var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-md)' }} data-testid={`talent-card-${t.profileId}`}>
                <div className="flex items-start gap-3">
                  {/* Score ring */}
                  <div className="shrink-0">
                    {t.genreAlignmentScore != null ? (
                      <GenreScoreRing score={t.genreAlignmentScore} />
                    ) : (
                      <div className="flex items-center justify-center rounded-full" style={{ width: 48, height: 48, background: 'var(--s8-bg-card)', border: '1px solid var(--s8-b1-solid)' }}>
                        <Music className="h-4 w-4" style={{ color: 'var(--t3)' }} />
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold truncate" style={{ color: 'var(--t0)' }} data-testid={`talent-name-${t.profileId}`}>{t.talentName || 'Unknown'}</span>
                      {t.travelConfirmed && (
                        <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold" style={{ color: 'var(--role-covered)', border: '1px solid rgba(90,154,107,0.3)', background: 'rgba(90,154,107,0.08)', borderRadius: 'var(--s8-radius-xs)' }} data-testid={`travel-confirmed-${t.profileId}`}>
                          <CheckCircle2 className="h-2.5 w-2.5" /> Travel OK
                        </span>
                      )}
                    </div>
                    <div className="text-xs mb-1.5" style={{ color: 'var(--t2)' }}>
                      {t.talentCity && <>{t.talentCity}</>}
                      {t.talentRole && <><span style={{ color: 'var(--t3)' }}> · </span>{t.talentRole}</>}
                    </div>
                    {/* Genre alignment label */}
                    {t.genreAlignmentScore != null && (
                      <div className="text-[11px] mb-1" style={{ fontFamily: 'var(--s8-font-mono)', color: t.genreAlignmentScore >= 70 ? 'var(--role-covered)' : t.genreAlignmentScore >= 40 ? 'var(--t1)' : 'var(--t3)' }} data-testid={`genre-score-label-${t.profileId}`}>
                        {t.genreAlignmentScore}% genre match
                      </div>
                    )}
                    {/* Matching genres */}
                    {t.genreOverlap && t.genreOverlap.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`genre-overlap-${t.profileId}`}>
                        {t.genreOverlap.map((g, i) => (
                          <span key={`go-${g}-${i}`} className="px-1.5 py-0.5 text-[10px] capitalize" style={{ color: 'var(--t0)', border: '1px solid var(--s8-b2-solid)', background: 'rgba(241,236,221,0.06)', borderRadius: 'var(--s8-radius-xs)' }}>
                            {g}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* All talent genres (non-matching shown dimmer) */}
                    {t.talentGenres && t.talentGenres.length > 0 && t.genreOverlap && t.talentGenres.filter(g => !t.genreOverlap.map(o => o.toLowerCase()).includes(g.toLowerCase())).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`talent-genres-${t.profileId}`}>
                        {t.talentGenres.filter(g => !t.genreOverlap.map(o => o.toLowerCase()).includes(g.toLowerCase())).map((g, i) => (
                          <span key={`tg-${g}-${i}`} className="px-1.5 py-0.5 text-[10px]" style={{ color: 'var(--t3)', border: '1px solid var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-xs)' }}>
                            {g}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Travel info */}
                    {t.travelConfirmed && t.travelMode && (
                      <div className="flex items-center gap-2 mt-2 text-[11px]" style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--t3)' }} data-testid={`travel-info-${t.profileId}`}>
                        {t.departureCity && <span>From {t.departureCity}</span>}
                        <span>·</span>
                        <span>{t.travelMode === 'car' ? 'Driving' : t.travelMode === 'public' ? 'Transit' : t.travelMode === 'bike' ? 'Cycling' : t.travelMode}</span>
                        {t.estimatedTravelMinutes && <><span>·</span><span>{t.estimatedTravelMinutes} min</span></>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      </div>{/* end detail-main */}

      {/* ═══ DETAIL SIDEBAR (desktop) ═══ */}
      <div className="detail-sidebar space-y-6">

      {/* Travel Planning in sidebar */}
      {request.userHasInterest && !isExpired && (
        <Section tier="standard">
          {/* The title is the card's direct child so it takes the same title band as every
              other card; the control moves into the body as a raised button. */}
          <SectionHeader icon={Navigation}>Travel Planning</SectionHeader>
          {!showTravelPlanning && (
            <button type="button" onClick={() => setShowTravelPlanning(true)}
              className="rd-ghost-btn rd-key w-full flex items-center justify-center gap-2 px-4 text-sm font-medium outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--role-money)]"
              style={{ minHeight: '44px', borderRadius: '22px' }} data-testid="plan-travel-btn">
              <Navigation className="h-4 w-4 shrink-0" style={{ color: 'var(--t1)' }} aria-hidden />
              Plan your journey
            </button>
          )}
          {showTravelPlanning && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label style={{ color: 'var(--t2)' }} className="text-sm">How Will You Travel?</Label>
                <RadioGroup value={selectedTransportMode} onValueChange={v => { setSelectedTransportMode(v); setTravelEstimate(null); }} className="flex gap-4">
                  {[['car', Car, 'Car'], ['public', Train, 'Public'], ['bike', Bike, 'Bike']].map(([val, Icon, label]) => (
                    <div key={val} className="flex items-center space-x-2">
                      <RadioGroupItem value={val} id={`sidebar-${val}`} />
                      <Label htmlFor={`sidebar-${val}`} className="flex items-center gap-1.5 cursor-pointer text-sm" style={{ color: 'var(--t2)' }}>
                        <Icon className="h-3.5 w-3.5" /> {label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              {showTransitWarning && <p className="text-sm" style={{ color: 'var(--role-limit)' }}>Public transit access is poor for this venue.</p>}
              {!travelEstimate && (
                <Button onClick={handleEstimateTravel} disabled={estimating} className="w-full" style={{ background: 'var(--bg-card-inner)', color: 'var(--t1)', border: '1px solid var(--s8-b1-solid)' }} data-testid="estimate-travel-btn">
                  {estimating ? 'Calculating...' : 'Calculate Travel Time'}
                </Button>
              )}
              {travelEstimate && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <DataPocket label="Distance"><div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: 'var(--s8-fs-lead)', color: 'var(--t0)' }}>{travelEstimate.distanceKm} km</div></DataPocket>
                    <DataPocket label="Travel Time"><div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: 'var(--s8-fs-lead)', color: 'var(--t0)' }}>{travelEstimate.durationMinutes} min</div></DataPocket>
                  </div>
                  {travelEstimate.isEstimated && <p className="text-xs" style={{ color: 'var(--t3)' }}>Transit estimated from driving time (+30%).</p>}
                  {travelEstimate.bikeWarning && <p className="text-xs" style={{ color: 'var(--t1)' }} data-testid="bike-warning">{travelEstimate.bikeWarning}</p>}
                  <div className="pt-3" style={{ borderTop: '1px solid var(--s8-b1-solid)' }}>
                    <p className="text-xs text-center mb-3" style={{ color: 'var(--t3)' }}>Add Gig + Travel to Calendar</p>
                    <div className="flex gap-3">
                      <Button onClick={handleAppleCalendar} disabled={downloading} variant="outline" className="flex-1 border-[var(--s8-b1-solid)] hover:bg-white/[0.04]" data-testid="apple-calendar-btn">
                        {downloading ? '...' : 'Apple'}
                      </Button>
                      <Button onClick={handleGoogleCalendar} variant="outline" className="flex-1 border-[var(--s8-b1-solid)] hover:bg-white/[0.04]" data-testid="google-calendar-btn">
                        Google
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {/* Available Musicians/Tech in sidebar */}
      {user?.profileType === 'VENUE' && request.availableProfiles && request.availableProfiles.length > 0 && (
        <Section tier="standard">
          <SectionHeader icon={Users}>Available {request.roleTypeNeeded === 'MUSICIAN' ? 'musicians' : 'tech crew'}</SectionHeader>
          <div className="space-y-3">
            {request.availableProfiles.map(p => (
              <Link key={p.id} to={`/${request.roleTypeNeeded === 'MUSICIAN' ? 'musicians' : 'tech'}/${p.id}`} className="block p-3.5 transition-colors hover:border-[var(--s8-b2-solid)]" style={{ background: 'var(--bg-card-inner)', border: '1px solid var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-md)' }} data-testid={`available-profile-${p.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold" style={{ color: 'var(--t0)' }}>{p.displayName}</span>
                  {p.gigCount > 0 && <span className="px-1.5 py-0.5 text-[11px]" style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--t2)', border: '1px solid var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-xs)' }}>{p.gigCount} gigs</span>}
                </div>
                <div className="text-xs mb-1.5" style={{ color: 'var(--t2)' }}>
                  {p.city && <>{p.city}, NL</>}{p.travelRadius ? ` · ${p.travelRadius}km radius` : ''}
                </div>
                {p.rate && <div className="text-xs" style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--role-money)' }}>€{p.rate} per gig</div>}
                {p.specialties && p.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.specialties.slice(0, 4).map((s, i) => <span key={`sk-${s}-${i}`} className="px-1.5 py-0.5 text-[11px]" style={{ color: 'var(--t2)', border: '1px solid var(--s8-b1-solid)', borderRadius: 'var(--s8-radius-xs)' }}>{s}</span>)}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </Section>
      )}
      </div>{/* end detail-sidebar */}
      </div>{/* end detail-layout */}

      {/* ── ADMIN CONTROLS ─────────────────────────────────── */}
      {user?.isAdmin && (
        <div className="mt-6 pt-4 flex items-center justify-between max-w-2xl" style={{ borderTop: '1px solid rgba(212,84,58,0.15)' }} data-testid="admin-controls">
          <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(212,84,58,0.6)' }}>
            <ShieldAlert className="h-3 w-3" /><span>Admin</span>
          </div>
          {request.hidden ? (
            <Button variant="outline" size="sm" className="text-xs" style={{ borderColor: 'var(--s8-b2-solid)', color: 'var(--t1)' }}
              onClick={async () => { try { await adminUnhideRequest(request.id); toast.success('Restored'); loadRequest(); } catch { toast.error('Failed'); } }} data-testid="admin-unhide-btn">
              <ShieldCheck className="h-3 w-3 mr-1" /> Restore
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="text-xs" style={{ borderColor: 'rgba(212,84,58,0.25)', color: 'var(--role-limit)' }}
              onClick={async () => { try { await adminHideRequest(request.id); toast.success('Hidden'); navigate('/'); } catch { toast.error('Failed'); } }} data-testid="admin-hide-btn">
              <ShieldAlert className="h-3 w-3 mr-1" /> Hide
            </Button>
          )}
        </div>
      )}

      {/* Travel Confirmation Modal */}
      <TravelConfirmationModal isOpen={showTravelModal} onClose={() => setShowTravelModal(false)} onConfirm={handleTravelConfirm} request={request} userCity={userProfile?.city} isLoading={interestLoading} />
    </div>
    </div>
  );
}
